import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { createDeviceLifecycleTracker } from '#~/analytics/domains/index.js';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import {
  ALERT_ACTION,
  ALERT_ACTION_RESULT,
  ALERT_CLOSE_REASON,
  ALERT_ID,
  ALERT_VARIANT,
  createAlertTracker,
  type AlertCloseReason,
  type AlertShowing
} from '#~/analytics/domains/alert-lifecycle.js';
import { useAgents } from '#~/agent/hooks.js';
import { Columns } from '#~/common/editor/index.js';
import { Brand, Model } from '#~/device/components/config/index.js';
// Direct module import: step suites mock the config barrel with Brand/Model only.
// eslint-disable-next-line import/no-internal-modules
import {
  NotListedBrandLabel,
  ReadOnlyValue
} from '#~/device/components/config/read-only-value.jsx';
import { useCatalogueModels } from '#~/device/registry/catalogue.js';
import { useBrands, useModels, useTemplate } from '#~/device/registry/hooks.js';
import type { IdentifiedBy } from '#~/analytics/domains/device-identification.js';
import { PostHogFeatureFlag } from '#~/posthog/constants.js';
import { useFeatureFlag } from '#~/posthog/hooks/use-feature-flag.js';
import { ManualSourceBridgeSelector } from '#~/video/components/add/source/bridge-selector.js';
import { DataSTag, Sel, STag, STagNew } from '#~/stag/tag.js';
import { locateProxy } from '#~/agent/index.js';
import {
  addCentralDispatchSubscriber,
  clearCentralDispatchSubscriber
} from '#~/statequery/central-dispatch.js';
import type { Device } from '@studio/models/device';
import {
  AuthenticationStatus,
  StatusDeviceConnectionStatus
} from '@tryiris-ai/common.protojs/agent/v1/agent';
import { Button } from '@studio/design-system/button';
import { Callout } from '@studio/design-system/callout';
import { Annotation, Column, Group, Message } from '@studio/design-system/form';
import { Input } from '@studio/design-system/input';
import { Label } from '@studio/design-system/label';
import { Spinner } from '@studio/design-system/spinner';
import type { HtmlAttributes } from '@studio/design-system/props';
import { Tag } from '@studio/design-system/tag';
import * as Select from '@studio/design-system/select';
import { formatUri, parseUri } from '@studio/models/net';
import type { Brand as M_Brand, Model as M_Model } from '@studio/models/device';
import { Protocol as ControlProtocol } from '@studio/models/control';
import { HttpDefaults } from '@studio/models/control/http';
import { isCausedBy } from '@studio/js-utils/logger';
import { Protocol, toProtocol } from '@studio/models/video';
import {
  foldRtspCredentials,
  isRtsp,
  maskRtspUrl,
  stripRtspCredentials,
  toConfiguration,
  toUri
} from '@studio/models/video/rtsp';
import type { Configuration as RtspConfig } from '@studio/models/video/rtsp';
import { toAccessor } from '@studio/react-utils';
import { useLatestFn, useUpdateEffect } from '@studio/react-utils/hooks';
import { toString } from '@tryiris-ai/js-types';
import type { T_Maybe, T_Optional } from '@tryiris-ai/js-types';
import { shallowEqual } from 'fast-equals';
// cn re-exports twMerge (cnfast was removed by an earlier base migration).
import { twMerge } from 'cn';
import type { Opaque, RequireAtLeastOne } from 'type-fest';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useId, useMemo, useReducer, useRef, useState } from 'react';
import { shallow } from 'zustand/shallow';
import { v4 as uuid } from 'uuid';
import { Placeholder, Preview } from '../../preview/index.js';
import type { IdentifiedDiscovery } from '../../identification/identify-at-host.js';
import { useIdentifyAtHost } from '../../identification/identify-at-host.js';
import type { Detected } from '../../manufacturer/utils.js';
import {
  findBrandIdForModel,
  getDefaultName,
  getDetected,
  getPickedIdentifiedBy,
  useExternalModelId
} from '../../manufacturer/utils.js';
import {
  isNotListedAnswer,
  markIdentifiedBy,
  openNotListed,
  reopenNotListed
} from '../../store/identification.slice.js';
import { useProducer, useStore } from '../../store/hooks.js';
import { requestPreviewRetry } from '../../store/preview-prepare.js';
import type { Draft } from '../../store/types.js';
import { Panel } from '../../store/types.js';
import {
  AttemptingConnection,
  CameraBrand,
  CameraModel,
  CancelAttempt,
  CheckConnectivity,
  ControlAuthInvalidMessage,
  Credentials,
  DevicePassword,
  DeviceUsername,
  IpAddress,
  OtherBrandRecommendation,
  PreviewAuthRejectedMessage,
  PreviewAuthRequiredMessage,
  PreviewConnectionFailedMessage,
  StreamFormat,
  StreamFormatPicker,
  StreamFormatPickerHint,
  StreamUrl
} from './constants.js';
import {
  authStatusMessage,
  buildCatalogOptions,
  buildTemplateOptions,
  classifyConnectionFailure,
  connectionFailureMessage,
  findGenericModelId,
  isAutoAttemptUrl,
  isCheckConnectivityEnabled,
  IpAddressRequiredMessage,
  ipAddressError,
  isOtherBrand,
  parseHostPort,
  probeWarningMessage
} from './utils.js';
import {
  Phase,
  classifyHttpEmpty,
  classifyRtspEmpty,
  initialProbeState,
  phase2NeedsHttp,
  probeReducer,
  urxHasCredentials
} from './auth-probe.js';

/** Local brand/model selection mirror (relocated from manufacturer/details.tsx). */
type Selection = { brandId?: M_Brand['id'] | undefined; modelId?: M_Model['id'] | undefined };

/**
 * Field label with the "Detected" pill after it (ENG-4830 Board A on the RTSP
 * step). Mirrors manufacturer/details.tsx `FieldLabel`; rendered only while the
 * field IS detected, so an undetected field keeps its plain string label (what
 * the step's `data-label` lookups read).
 *
 * Per rule: ui-architecture (ai.knowledge/global) — the pill is the design-system
 * `Tag` with the theme's success tone, so Iris and Hive share one component.
 * Per rule: playwright-stag-selectors-only (ai.knowledge/platform) — the pill
 * carries the same `detected-brand` / `detected-model` STags as the Manufacturer step.
 */
const DetectedLabel = ({
  field,
  label
}: {
  field: keyof Detected;
  label: string;
}): ReactElement => (
  <Label className='items-center gap-8'>
    {label}
    <Tag
      className='px-8 py-2 text-xs font-medium'
      message='Detected'
      severity='success'
      {...STag(Sel.ConfigVideoDeviceBrandModel, `detected-${field}`)}
    />
  </Label>
);

const RTSP_URL_IN_TEXT = /\brtsp[a-z]*:\/\/\S+/gi;
// Suppression lasts for this app/page session, including closing and reopening Add.
const n7LastShownAt = new Map<string, number>();

/**
 * Redact RTSP credentials from a free-form error message before display.
 *
 * A commit/agent error is keyed by the credential-bearing stream URL, so its
 * message can echo a raw `rtsp://user:pass@host`. Rendered verbatim, the real
 * password would land on screen — defeating this wizard's "never render RTSP
 * credentials" goal. Every embedded RTSP URL is redacted through the shared
 * `maskRtspUrl` (masking is delegated, never reinvented) while the surrounding
 * prose is left intact so the error stays useful. This guards the "never render
 * RTSP credentials" invariant — do not render a commit error without it.
 */
const maskRtspCredentialsInMessage = (message: string): string =>
  message.replace(RTSP_URL_IN_TEXT, (match) => {
    // Peel trailing sentence punctuation (a period, or the `:` before a
    // trailing clause) so it stays outside the URL and the message still reads
    // as a sentence after masking.
    const trailing = /[).,;:'"\]}>]+$/.exec(match)?.[0] ?? '';
    const url = trailing ? match.slice(0, -trailing.length) : match;
    return `${maskRtspUrl(url) ?? url}${trailing}`;
  });

/**
 * `<Rtsp>` video component — RTSP Assisted Setup.
 *
 * Reworks the raw Stream-URL screen into a guided brand→model→IP→credentials
 * flow. Brand/model selection is RELOCATED here from the (removed) Manufacturer
 * wizard panel (only the RTSP path changes — NDI/local are isolated siblings in
 * add/video/content.tsx and stay visually unchanged).
 *
 * Three resolution cases (see ./utils.ts):
 *   1. Known brand + specific model → useTemplate(modelId).video → Main(+Sub).
 *   2. Known brand + Model="Other"  → brand's generic template (the model whose
 *      name says "Other" — *_generic.go record) → useTemplate(genericId).video.
 *   3. Brand="Other"                → hide Model selector; show the hardcoded
 *      common-format picker.
 *
 * candidate.video is written ONLY by the existing Check-Connectivity handler
 * (no new writer). The credential inputs READ candidate.video.credentials with a
 * fallback to candidate.control.credentials (the ONVIF auth prefill) — they
 * never write; their values feed composeRtspUrl, which the Check-Connectivity
 * write folds into candidate.video.
 */
export const Rtsp = (props: HtmlAttributes): ReactElement => {
  const producer = useProducer();

  // Video step only: Scaffold keeps this helper mounted through its exit fade, and after
  // Back the Source step rebuilds the candidate from the row. A reset, probe or model write
  // from the fading helper must not land there. The step is read from the draft at write time.
  const writeOnVideoStep = useCallback(
    (recipe: (draft: Draft) => void): void => {
      producer((draft) => {
        if (draft.panel === Panel.Video) recipe(draft);
      });
    },
    [producer]
  );

  const { track } = useAnalytics();
  const deviceLifecycleTracker = createDeviceLifecycleTracker(track);
  const alertTracker = createAlertTracker(track);
  const { enabled: n7Enabled, loading: n7Loading } = useFeatureFlag('alert_n7_enabled', {
    defaultValue: true
  });
  const showN7 = n7Enabled === true && !n7Loading;
  const agentId = useStore((state) => state.agentId);
  const roomId = useStore((state) => state.roomId);
  const previewPending = useStore((state) => state.previewPending);
  const previewAvailability = useStore((state) => state.previewAvailability);
  const bridgeName = useAgents((agents) => agents.find((agent) => agent.id === agentId)?.name);
  const [n7, setN7] = useState<AlertShowing | undefined>();
  const n7Ref = useRef<AlertShowing | undefined>(undefined);
  const [n7Retrying, setN7Retrying] = useState(false);
  const n7Retry = useRef<{ showing: AlertShowing; deviceId: string | undefined } | undefined>(
    undefined
  );
  const closeN7 = useLatestFn((reason: AlertCloseReason) => {
    if (n7Retry.current) {
      alertTracker.trackAlertActed(n7Retry.current.showing, {
        action: ALERT_ACTION.TRY_AGAIN,
        result: ALERT_ACTION_RESULT.FAILURE
      });
      n7Retry.current = undefined;
      setN7Retrying(false);
    }
    if (n7Ref.current) {
      alertTracker.trackAlertClosed(n7Ref.current, reason);
      n7Ref.current = undefined;
      setN7(undefined);
    }
  });
  useEffect(() => () => closeN7(ALERT_CLOSE_REASON.SUPERSEDED), []);

  const { enabled: isBridgeEnabled } = useFeatureFlag(PostHogFeatureFlag.BRIDGE_FEATURE, {
    defaultValue: false
  });

  // Initial store reads for one-time seeding (ONVIF prefill). candidate.video is
  // the ONVIF/manual prefill source; control.credentials is the fallback when
  // the user advances before ONVIF caps propagate creds onto candidate.video.
  const seed = useStore((µ) => {
    // This screen is RTSP-only (content.tsx renders it only when the protocol
    // is RTSP), so the video/control aspect configs are read as RTSP-shaped for
    // host/credentials extraction.
    const video = µ.candidate.video as T_Maybe<RtspConfig>;
    const control = µ.candidate.control as T_Maybe<{
      credentials?: T_Maybe<RtspConfig['credentials']>;
    }>;

    return {
      host: video?.host,
      modelId: µ.candidate.modelId,
      // Brand-level fingerprint seed: set by panel.tsx when the user clicked a
      // brand-only discovery row (brandId, no modelId). Read
      // here to pre-seed the Brand dropdown on mount, leaving the model for
      // the user to pick — mirrors the `template.model.brandId` seed effect
      // below (used for the full-model / ONVIF-resolved path).
      seededBrandId: µ.source?.seededBrandId,
      // Brand-only host seed: the discovered host for a brand-only match,
      // stashed out-of-band by panel.tsx so the IP field pre-fills WITHOUT a
      // connectable `candidate.video` (which would spin up a doomed preview
      // device and flash "Connection failed"). Falls back to the candidate
      // video host for the known-model / ONVIF paths.
      seededHost: µ.source?.seededHost,
      videoCreds: video?.credentials,
      controlCreds: control?.credentials,
      // ONVIF prefill: an ONVIF discovery writes the stream URI the camera reported
      // (host + PATH + creds + query) to candidate.video. Mirror the old screen's
      // `actualUrl` by composing the URL from the WHOLE candidate video so the
      // manual-URL channel can seed once on mount. A row with no registry model has
      // no stream picker options (`selectedUrl` is undefined), so this seed is what
      // the field shows. A fingerprinted ONVIF row carries a modelId: its template
      // seeds the brand, and the overwrite-on-select effect below then drops this
      // seed for the template's stream. GATE on `path` (not just host) so bare-host
      // flows (known-model — the picker composes the URL) do NOT enter manual mode;
      // only an ONVIF candidate carrying a path seeds it.
      //
      // Never render real RTSP credentials: the manual-edit field is
      // credential-FREE. The discovered creds are seeded separately into
      // the username/password state below (from video.credentials) and are
      // re-folded into the effective URL at compose/commit time. So strip the
      // `user:pass@` segment from the seed here.
      seededUrl:
        video?.host && video?.path ? stripRtspCredentials(formatUri(toUri(video))) : undefined
    };
  }, shallow);

  // Preview gate + the kept ONVIF Stream-URL prefill read: Preview renders once
  // candidate.video carries a committed config (set by Check Connectivity or an
  // ONVIF prefill).
  const hasVideo = useStore((µ) => !!(µ.candidate.video as T_Maybe<RtspConfig>)?.host);

  // Commit-error + video auth-status messaging (consume-only; mirrors the
  // control screen's Callout). The commit error is captured into
  // previews.video.error by mediator.tsx's syncCandidate try/catch — we only
  // READ it here.
  const {
    commitError,
    authStatus,
    deviceId,
    videoError,
    videoUrx,
    controlAuthStatus,
    controlUrx,
    controlError
  } = useStore((µ) => {
    const deviceId = µ.previews?.video?.deviceId;
    // Empty-creds-probe HTTP discriminator: the control-plane probe
    // spins up a SEPARATE preview device (previews.control), whose
    // controlAuthenticationStatus is the HTTP verdict the auth-probe machine
    // classifies. Undefined until a modelSelected probe writes candidate.control.
    const controlDeviceId = µ.previews?.control?.deviceId;

    return {
      commitError: µ.previews?.video?.error,
      authStatus: deviceId ? µ.external.transients[deviceId]?.videoAuthenticationStatus : undefined,
      deviceId,
      // The encoder's error for the previewed stream (e.g. the GStreamer failure
      // cause) — classified only once the attempt has failed, to pick guidance.
      videoError: deviceId ? µ.external.transients[deviceId]?.video?.error : undefined,
      // The preview URX carries the probe's credentials in its authority (or
      // not). Correlating each verdict to the URX lets each phase accept ONLY its
      // own transient's verdict — never a stale one left over from the previous
      // phase's async mediator teardown.
      videoUrx: µ.previews?.video?.urx,
      controlAuthStatus: controlDeviceId
        ? µ.external.transients[controlDeviceId]?.controlAuthenticationStatus
        : undefined,
      controlUrx: µ.previews?.control?.urx,
      // Synchronous control-plane failures arrive here, NOT as a transient
      // status. A 400/401/403 is a definite auth rejection.
      controlError: µ.previews?.control?.error
    };
  }, shallow);

  // A control preview error that is a 400/401/403 is a definite auth rejection,
  // surfaced through this channel instead of a transient status. Classify it so
  // the probe never waits the full 15s and degrades a real rejection to
  // HTTP_UNKNOWN.
  const controlErrorAuthFail =
    !!controlError &&
    (isCausedBy(controlError as Error, '400: Bad Request') ||
      isCausedBy(controlError as Error, '401: Unauthorized') ||
      isCausedBy(controlError as Error, '403: Forbidden'));

  // Live VIDEO connection status for the previewed device — the SAME per-device
  // status that drives the preview's "Video Connection Failed" badge. The
  // badge (device/components/status/status.tsx) reads it directly off the
  // managing proxy's state-sync state via locateProxy, NOT via
  // useMultiDeviceStateQuery: multiDeviceStateQuery only iterates devices
  // present in `control.device`, whereas the transient preview device's live
  // video status lives under `status.device[deviceId]`. We mirror the badge's
  // exact source so the form error tracks the badge, subscribing to central
  // dispatch for re-renders.
  const [videoConnStatus, setVideoConnStatus] = useState<StatusDeviceConnectionStatus | undefined>(
    undefined
  );

  useEffect(() => {
    if (!deviceId) {
      setVideoConnStatus(undefined);
      return;
    }

    const read = (): StatusDeviceConnectionStatus | undefined =>
      locateProxy(deviceId as Device['id'])?.state.status?.device?.[deviceId as Device['id']]?.video
        ?.connectionStatus?.status;

    setVideoConnStatus(read());

    const subscriberId = addCentralDispatchSubscriber(() => {
      setVideoConnStatus(read());
    }, 500);

    return () => {
      clearCentralDispatchSubscriber(subscriberId);
    };
  }, [deviceId]);

  // STICKY connection-error state. When a device is added the encoder cycles
  // CONNECTING → FAILED → (retry) CONNECTING → FAILED…; without stickiness the
  // form error would flicker away on every FAILED→CONNECTING transition. So we
  // latch on FAILED and clear ONLY on CONNECTED (or an explicit re-check / a
  // device change) — CONNECTING / UNKNOWN / undefined leave it as-is.
  const [connectionFailed, setConnectionFailed] = useState(false);
  const [connectionTimedOut, setConnectionTimedOut] = useState(false);

  // Empty-creds-probe machine (IDLE → PROBE_EMPTY → PROBE_REAL → DONE). Pure
  // transition logic lives in auth-probe.ts; this component owns the
  // side effects (candidate.video/control writes that spin up the probe
  // transients, the phase timeout) and feeds the transient verdicts back in as
  // events. See the probe orchestration effects below. The bug it fixes: an
  // "optional" credentials section means a server that IGNORES creds still
  // reaches Playing, so VALIDATED cannot distinguish "creds validated" from
  // "creds ignored" — the empty-creds probe is the discriminator.
  const [probe, dispatchProbe] = useReducer(probeReducer, initialProbeState);

  // Joint phase timeout (mirrors discovery.tsx STALL_TIMEOUT_MS): a probe phase
  // waits at most 15s for BOTH aspects (RTSP + HTTP-if-applicable) to reach a
  // terminal verdict, so a transient that never resolves can't stall the wizard.
  //
  // The timeout is GENERATION-scoped. `timedOutGen` records which probe
  // generation's 15s timer fired; the phase is treated as timed-out only while
  // that equals the CURRENT `probe.generation`. A Phase-1 timeout (an older
  // generation) can therefore never force a terminal verdict on Phase 2 —
  // otherwise Phase 2 would abort the instant it began off Phase 1's stale
  // `phaseTimedOut=true`.
  const PROBE_PHASE_TIMEOUT_MS = 15000;
  const [timedOutGen, setTimedOutGen] = useState<number | undefined>(undefined);
  const phaseTimedOut = timedOutGen === probe.generation;

  // Arm a fresh timer whenever the machine ENTERS an active probe phase, scoped
  // to the generation current at arm time. The cleanup clears the timer on every
  // phase/generation change (and on unmount), so exactly one timer is ever live
  // and a phase the machine has already left can never fire its timeout.
  useEffect(() => {
    if (probe.phase !== Phase.PROBE_EMPTY && probe.phase !== Phase.PROBE_REAL) {
      return undefined;
    }
    const generation = probe.generation;
    const timer = setTimeout(() => {
      setTimedOutGen(generation);
    }, PROBE_PHASE_TIMEOUT_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [probe.phase, probe.generation]);

  // Guards each phase-entry side effect to fire exactly once per entry (the
  // orchestration effect below re-runs on value changes, but must write the
  // candidate only on the transition INTO a phase).
  const enteredPhaseRef = useRef<Phase | undefined>(undefined);

  // GRACE/DEBOUNCE on the FAILED latch. On a SUCCESSFUL connect the
  // encoder reports a TRANSIENT FAILED partway through the handshake before
  // settling on CONNECTED — measured ~2s in, clearing ~3.6s later. Latching
  // immediately on that transient FAILED flashed a "Connection failed" Callout
  // for ~3-4s on a connection that ultimately succeeded. So instead of latching
  // on the first FAILED, we start a grace timer; we only latch if the status is
  // STILL not CONNECTED when the timer fires. A genuine failure cycles
  // FAILED→CONNECTING→FAILED and never reaches CONNECTED, so the timer fires and
  // latches; CONNECTED arriving first cancels the timer and suppresses the flash.
  // 4000ms > the measured ~3.6s flash window (+ margin).
  const CONNECTION_FAILED_GRACE_MS = 4000;

  // Hold the pending grace timer, the latest live status (so the timer callback
  // can re-read the CURRENT status — no stale-closure read), and the PREVIOUS
  // status (so the grace timer is armed only on a transition INTO FAILED, not on
  // every render where the status happens to be FAILED — which matters for the
  // Check-Connectivity re-arm, where the status may stay FAILED but the latch
  // must NOT spontaneously re-arm without a fresh FAILED edge).
  const connectionFailedTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const videoConnStatusRef = useRef<StatusDeviceConnectionStatus | undefined>(videoConnStatus);
  videoConnStatusRef.current = videoConnStatus;
  const prevVideoConnStatusRef = useRef<StatusDeviceConnectionStatus | undefined>(undefined);

  // The REAL "stream is playing" signal (STREAM_STATE_PLAYING), the same field
  // panel.tsx folds into video.isReady. connectionStatus (the enum
  // driving the latch below) has no "playing" value — for an ONVIF seed the
  // stream renders frames while connectionStatus never reaches CONNECTED, so the
  // latch must be gated on this to avoid a false "Connection failed". Mirrored
  // into a ref so the timer callbacks read a fresh value without a stale closure.
  const videoIsPlaying = useStore((µ) => µ.video.isReady);
  const videoIsPlayingRef = useRef(videoIsPlaying);
  videoIsPlayingRef.current = videoIsPlaying;

  // Never-edge backstop: the sticky FAILED latch above reacts ONLY to a
  // FAILED/CONNECTED *edge*. For some Brand="Other" formats the encoder never
  // emits either edge — the video pipeline never populates status.Video, so
  // videoConnStatus is stuck at CONNECTING or stays undefined forever. With no
  // edge nothing latches and no CONNECTED clears the spinner, so the preview
  // spins indefinitely. This bounded timer surfaces the SAME "Connection failed"
  // Callout the FAILED latch produces when the previewed device never reaches
  // CONNECTED.
  //
  // The timeout must exceed the encoder's own failure horizon so it never
  // pre-empts a truthful encoder FAILED nor regresses a slow-but-valid connect
  // into a false failure. The encoder forces CONNECTING for its firstConnectTimeout
  // (30s) then emits FAILED after a further ~5s (status.go) → a ~35s
  // horizon; 30s is the longest known-good negotiation window. 35s = the
  // encoder failure horizon exactly — a 5s cushion over the known-good
  // window. In the normal case the encoder's FAILED (≤35s) trips the 4000ms
  // grace latch first; this only ever fires for the pathological never-edge
  // case (encoder emits no video status at all).
  const CONNECTING_TIMEOUT_MS = 35000;
  const connectingTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Clear any pending grace timer + the latched flag. Shared by the device-change
  // reset, the Check-Connectivity re-arm, and unmount cleanup.
  const clearConnectionFailure = useCallback(() => {
    if (connectionFailedTimerRef.current !== undefined) {
      clearTimeout(connectionFailedTimerRef.current);
      connectionFailedTimerRef.current = undefined;
    }
    // Cancel the CONNECTING backstop too, so there is ONE coherent latch —
    // both the FAILED grace timer and the never-edge backstop
    // clear on CONNECTED, on device change, and on an explicit re-check.
    if (connectingTimerRef.current !== undefined) {
      clearTimeout(connectingTimerRef.current);
      connectingTimerRef.current = undefined;
    }
    setConnectionFailed(false);
    setConnectionTimedOut(false);
  }, []);

  useEffect(() => {
    // Reset the sticky flag + cancel any pending grace/backstop timers when the
    // previewed device changes so an error does not leak across devices/previews.
    // Also forget the previous-status edge so a fresh device re-arms cleanly.
    clearConnectionFailure();
    prevVideoConnStatusRef.current = undefined;

    // Arm the CONNECTING backstop here — on device-PRESENT, NOT on the first
    // CONNECTING edge. Arming in this effect gives ARM-ONCE for free: the effect
    // depends only on deviceId, so a FAILED→CONNECTING retry bounce (which the
    // status effect intentionally ignores) can NEVER re-arm or reset it, and it
    // cannot race the 4000ms FAILED grace timer. It also covers the
    // undefined-forever case (encoder emits no video status at all), which an
    // "on first CONNECTING" hook would miss. Fires iff the device never reached
    // CONNECTED by the timeout.
    if (deviceId) {
      connectingTimerRef.current = setTimeout(() => {
        connectingTimerRef.current = undefined;
        if (
          videoConnStatusRef.current !== StatusDeviceConnectionStatus.CONNECTION_STATUS_CONNECTED &&
          !videoIsPlayingRef.current
        ) {
          setConnectionTimedOut(true);
          setConnectionFailed(true);
        }
      }, CONNECTING_TIMEOUT_MS);
    }
  }, [deviceId, clearConnectionFailure]);

  useEffect(() => {
    const prev = prevVideoConnStatusRef.current;
    prevVideoConnStatusRef.current = videoConnStatus;

    if (videoConnStatus === StatusDeviceConnectionStatus.CONNECTION_STATUS_CONNECTED) {
      // Success path: cancel any pending grace timer AND clear the latch —
      // suppresses a transient-FAILED flash that resolved to CONNECTED.
      clearConnectionFailure();
    } else if (
      videoConnStatus === StatusDeviceConnectionStatus.CONNECTION_STATUS_FAILED &&
      prev !== StatusDeviceConnectionStatus.CONNECTION_STATUS_FAILED &&
      connectionFailedTimerRef.current === undefined &&
      // Phase-1 suppression: during the empty-creds probe a server that ENFORCES
      // auth deterministically FAILS the anonymous connection — that is the
      // probe's signal, NOT a user-facing error. Do not arm the "Connection
      // failed" latch while PROBE_EMPTY is in flight; the real-creds Phase 2 (or
      // a normal check) surfaces genuine failures.
      probe.phase !== Phase.PROBE_EMPTY
    ) {
      // Transition INTO FAILED (a fresh FAILED edge), and no grace timer already
      // pending: start the grace timer instead of latching immediately. The
      // timer re-reads the CURRENT status when it fires and only latches if we
      // are still not CONNECTED (a genuine failure never reaches CONNECTED).
      connectionFailedTimerRef.current = setTimeout(() => {
        connectionFailedTimerRef.current = undefined;
        if (
          videoConnStatusRef.current !== StatusDeviceConnectionStatus.CONNECTION_STATUS_CONNECTED &&
          !videoIsPlayingRef.current
        ) {
          setConnectionFailed(true);
        }
      }, CONNECTION_FAILED_GRACE_MS);
    }
    // CONNECTING / UNKNOWN / undefined (and a repeated FAILED while the timer is
    // pending) → do NOT cancel the timer: a genuine failure cycles
    // FAILED→CONNECTING→FAILED and must still latch when the timer fires.
  }, [videoConnStatus, clearConnectionFailure, probe.phase]);

  // A genuinely-playing stream (STREAM_STATE_PLAYING → video.isReady) may never
  // report connectionStatus === CONNECTED (e.g. ONVIF, whose SOAP
  // negotiation keeps connectionStatus non-CONNECTED past the 35s backstop while
  // frames already render). Treat "actually playing" as success so neither the 4s
  // grace nor the 35s backstop latches a false "Connection failed".
  useEffect(() => {
    if (videoIsPlaying) clearConnectionFailure();
  }, [videoIsPlaying, clearConnectionFailure]);

  // Cleanup on unmount: drop any pending grace timer AND the CONNECTING
  // backstop timer (no leaks).
  useEffect(
    () => () => {
      if (connectionFailedTimerRef.current !== undefined) {
        clearTimeout(connectionFailedTimerRef.current);
        connectionFailedTimerRef.current = undefined;
      }
      if (connectingTimerRef.current !== undefined) {
        clearTimeout(connectingTimerRef.current);
        connectingTimerRef.current = undefined;
      }
    },
    []
  );

  const brands = useBrands();

  // Brand/model selection state, seeded once from the prefilled model id.
  const [selection, setSelection] = useState<Selection>({ modelId: seed.modelId });
  const models = useModels(selection.brandId);

  // ENG-4830 / ENG-4831: what discovery had already resolved when the step
  // opened, for the "Detected" pills. `identifiedBy` and the model are a mount
  // snapshot — this step writes both as soon as the operator picks a model, so
  // reading them live would turn every search pick into a "Detected" one. The
  // brand-only seed is read live: nothing on this step writes it, and it may
  // land a tick after mount (the brand seed effect below follows it the same way).
  // Mirrors manufacturer/details.tsx. Per rule: think-in-systems (ai.knowledge/global).
  // ENG-4927: an identify-at-address hit on this step IS a detection, so it
  // re-snapshots `entry` (the only other writer) and the pills follow.
  const entryIdentifiedBy = useStore((µ) => µ.identification?.identifiedBy);
  const [entry, setEntry] = useState(() => ({
    identifiedBy: entryIdentifiedBy,
    modelId: seed.modelId
  }));
  const [changed, setChanged] = useState<Partial<Detected>>({});
  const entryDetected = getDetected({ ...entry, seededBrandId: seed.seededBrandId });
  const detected = getDetected({ ...entry, seededBrandId: seed.seededBrandId }, changed);

  // ENG-4831 B3: cached models per brand, so a model-name query in the brand
  // field can offer "Brand · Model" rows. Warmed only once the user types in the
  // brand field; the depot slice is referentially stable between writes, which
  // is what the memoised <Brand> compares. Also the catalogue the not-listed
  // follower below resolves an external model's brand from.
  const [brandQuery, setBrandQuery] = useState('');
  const modelsByBrand = useCatalogueModels(brands, brandQuery);

  // The resolved registry template for the selected model (specific OR the
  // brand's generic "Other" model — both flow through useTemplate). Already
  // pbToJsVideoTemplate-shaped: { host, path, pathSub, port, portSub }.
  const template = useTemplate(selection.modelId);

  // IP address (required) — seeded once from the prefilled host. The
  // brand-only entry path has no connectable `candidate.video`, so its host
  // arrives via the out-of-band `source.seededHost` seed; the known-model
  // / ONVIF paths still carry it on `candidate.video.host`.
  const [ipAddress, setIpAddress] = useState<string>(toString(seed.host ?? seed.seededHost) ?? '');

  // Credential inputs — READ-only binding (video creds, control-creds fallback).
  const [username, setUsername] = useState<string>(
    toString(seed.videoCreds?.username ?? seed.controlCreds?.username) ?? ''
  );
  const [password, setPassword] = useState<string>(
    toString(seed.videoCreds?.password ?? seed.controlCreds?.password) ?? ''
  );

  // The most recently Check-Connectivity-submitted URL (enablement predicate).
  const [lastCheckedUrl, setLastCheckedUrl] = useState<string | undefined>(undefined);

  // The last URL the wizard attempted on its own. Each URL is auto-attempted at
  // most once (a cancelled attempt is not re-run), and until it settles or the
  // user clicks, Check Connectivity stays enabled and a click adopts it.
  const [autoCheckedUrl, setAutoCheckedUrl] = useState<string | undefined>(undefined);

  const selectedBrand = selection.brandId ? brands?.items?.[selection.brandId] : undefined;
  const otherBrand = isOtherBrand(selectedBrand);

  const credentials = useMemo(() => ({ pass: password, user: username }), [password, username]);

  // The brand's generic ("Other") model id, if any. Used both to auto-select the
  // generic template (effect below) and to compute `modelSelected`.
  const genericModelId = useMemo(() => findGenericModelId(models), [models]);

  // `modelSelected`: a KNOWN brand + ANY model — the case where a registry HTTP
  // control module may exist to probe. A known brand + Model="Other" (the brand's
  // generic template) SHOULD count: its `*_generic.go` record can carry a real
  // `http_module_login`, so excluding it would lose the HTTP probe there. Only
  // the catch-all Brand="Other" (which auto-seeds a generic modelId but has no
  // per-brand control module) is excluded — `otherBrand` already does that, so
  // `genericModelId` is no longer subtracted. `genericModelId` is still used to
  // auto-select the generic template below.
  const modelSelected = !otherBrand && !!selection.modelId;

  // Whether the user has ENTERED credentials (drives the empty-creds probe). Any
  // non-empty dedicated field counts — the probe only runs when there is
  // something to validate.
  const hasEnteredCreds = !!(username || password);

  // Prefill guard: credentials seeded by ONVIF / a known model at MOUNT are
  // already validated by discovery, so the empty-creds probe is skipped for them
  // (no double latency, no spurious warning). The EXACT seeded values are
  // captured once at mount; the exemption applies ONLY while the current
  // username/password still equal them. A user who REPLACES a seeded credential
  // is entering an UNVALIDATED value, so the probe must run (else an edited seed
  // would ship unvalidated — a "sticky prefilled" flag would defeat the feature
  // for edited seeds).
  const seededCredsRef = useRef<{ username: string; password: string }>({
    username: toString(seed.videoCreds?.username ?? seed.controlCreds?.username) ?? '',
    password: toString(seed.videoCreds?.password ?? seed.controlCreds?.password) ?? ''
  });
  const hadSeededCreds = !!seededCredsRef.current.username || !!seededCredsRef.current.password;
  // Exempt only while BOTH fields still match the seeded values verbatim.
  const credsPrefilled =
    hadSeededCreds &&
    username === seededCredsRef.current.username &&
    password === seededCredsRef.current.password;

  // Stream options: catalog (Brand="Other") or template Main(+Sub).
  //
  // Brand-only-entry fix: when the user arrives via a brand-only discovery seed
  // and picks a model whose device-registry record
  // has NO RTSP video template (`pbToJsVideoTemplate` returns undefined for a
  // non-RTSP/empty `videoDriver` — e.g. a control/NDI-only registry entry),
  // `buildTemplateOptions(undefined, …)` returns `[]`. With no options there is
  // no `selectedUrl`, so the Stream URL field stays blank AND Check
  // Connectivity never enables — even though the IP + brand are populated. Fall
  // back to the common-format catalog (the SAME options the Brand="Other" path
  // composes) so the URL still composes from the entered IP + creds and the
  // user can proceed.
  //
  // An earlier fallback fired on
  // `!!selection.modelId && !templateVideo`, which CONFLATES two very different
  // states — (a) the template is still LOADING (`useTemplate` returns undefined
  // while it fetches async; the brand-only path never pre-fetched it, unlike
  // the known-model path where panel.tsx already primed the depot), and (b) the
  // template has RESOLVED with no RTSP video. In case (a) the fallback fired
  // PREMATURELY for a model that genuinely HAS a main/sub template (e.g.
  // Panasonic AW-UE100), showing the generic-URL catalog picker instead of the
  // real Main/Sub selector while the template loaded. So gate the fallback on
  // the template being RESOLVED (`template != null`) but VIDEO-LESS — i.e. only
  // fire once we KNOW the model has no RTSP template. While loading
  // (`template == null`) we render `buildTemplateOptions(undefined, …)` → `[]`
  // (blank, no catalog), and the useMemo recomputes to the real Main/Sub
  // options the moment `template.video` resolves. `template` here is the whole
  // resolved registry record `{ model, video? }` (undefined only while
  // fetching).
  const templateVideo = template?.video as T_Optional<RtspConfig>;
  const templateResolvedWithoutVideo = !!selection.modelId && !!template && !templateVideo;
  // Parse a user-entered `host:port` into a clean host + port. Passing the raw
  // `ipAddress` straight through embedded the `:port` inside the host and
  // dropped the user's port (the composer fell back to the template/catalog
  // default), producing malformed preset URLs. `composeHost` is blank for an
  // invalid host, which yields no options (composeRtspUrl returns undefined
  // without a host).
  const parsedIp = useMemo(() => parseHostPort(ipAddress), [ipAddress]);
  // Error text for the IP field, linked to the input via aria-describedby so
  // assistive tech reads why it is invalid. A malformed value is explained at
  // once; a blank one only after the user left the field or pressed Check
  // Connectivity, so a freshly opened form does not open on an error message.
  // The red border keeps its existing predicate (parsedIp.valid), which already
  // marks a blank field on open; this form has no touched state to gate it on.
  const [ipTouched, setIpTouched] = useState(false);
  const ipErrorCandidate = ipAddressError(ipAddress);
  const ipError =
    ipErrorCandidate === IpAddressRequiredMessage && !ipTouched ? undefined : ipErrorCandidate;
  const ipErrorId = useId();
  const composeHost = parsedIp.valid ? parsedIp.host : '';
  const options = useMemo(
    () =>
      otherBrand || templateResolvedWithoutVideo
        ? buildCatalogOptions(composeHost, credentials, parsedIp.port)
        : buildTemplateOptions(templateVideo, composeHost, credentials, parsedIp.port),
    [
      otherBrand,
      templateResolvedWithoutVideo,
      templateVideo,
      composeHost,
      parsedIp.port,
      credentials
    ]
  );

  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);

  // Keep the selected option valid as options change (default to first).
  useEffect(() => {
    if (!options.some((µ) => µ.id === selectedId)) {
      setSelectedId(options[0]?.id);
    }
  }, [options, selectedId]);

  const selectedUrl = options.find((µ) => µ.id === selectedId)?.url;

  /*
   * Stream-URL override channel (PM feedback pt 2 — the pencil toggle is gone
   * and the field is ALWAYS editable). `undefined` means "no override", so the
   * field mirrors the composed URL; ANY string, INCLUDING `''`, means the user
   * owns the value — hence `??` below and never `||`. The structured fields
   * still win: a change to IP / brand / model / selected stream drops the
   * override (overwrite-on-select; see the useUpdateEffect below). Credentials
   * deliberately do NOT — see the rationale on that effect.
   */
  // Seed the override ONCE on mount from the ONVIF-derived URL (lazy
  // initializer — never re-fires; a useEffect would either loop or clobber the
  // seed). lastCheckedUrl is intentionally NOT seeded so Check Connectivity is
  // enabled at mount for the discovered URL.
  const [manualUrl, setManualUrl] = useState<string | undefined>(() => seed.seededUrl);

  // Never render real RTSP credentials: the credential-FREE projection of the
  // composed URL. This is what the field shows when there is
  // no override, and the baseline every divergence check compares against.
  const composedDisplayUrl = stripRtspCredentials(selectedUrl) ?? '';

  // What the single, always-editable Stream URL input renders.
  const urlValue = manualUrl ?? composedDisplayUrl;

  // Derived "the user edited the URL" predicate — replaces the old `isManual`
  // mode flag for analytics. Both sides of the comparison are credential-free by
  // construction, so typing a password can never flip it.
  const hasManualUrl = urlValue !== composedDisplayUrl;

  // The URL every COMMIT/CONNECTIVITY consumer uses (with real creds): the
  // override re-folded with the dedicated DeviceUsername/DevicePassword creds,
  // else the composed URL VERBATIM. The no-override branch deliberately stays
  // `selectedUrl` rather than a strip→fold round-trip: the round-trip can change
  // the string byte-wise, which would spuriously reset candidate.video and
  // re-enable Check Connectivity.
  const effectiveUrl =
    manualUrl !== undefined ? foldRtspCredentials(manualUrl, credentials) : selectedUrl;
  const n7Attempt = useRef<{ url: string | undefined; agentId: typeof agentId } | undefined>({
    url: effectiveUrl,
    agentId
  });

  const openN7 = useLatestFn((variant: AlertShowing['alert_variant']) => {
    if (!showN7 || n7Ref.current) return;
    // The camera address is only an in-memory problem key, never event data.
    const key = `${roomId}:${agentId}:${urlValue}`;
    const now = Date.now();
    for (const [problem, shownAt] of n7LastShownAt) {
      if (now - shownAt >= 60_000) n7LastShownAt.delete(problem);
    }
    const previous = n7LastShownAt.get(key);
    if (previous !== undefined && now - previous < 60_000) return;
    const showing: AlertShowing = {
      alert_id: ALERT_ID.N7,
      showing_id: uuid(),
      ...(variant ? { alert_variant: variant } : {}),
      ...(agentId ? { agent_id: agentId } : {}),
      ...(roomId ? { studio_id: roomId } : {})
    };
    n7LastShownAt.set(key, now);
    n7Ref.current = showing;
    setN7(showing);
    alertTracker.trackAlertShown(showing);
  });
  // Classify the prefix independently of the host: parseUri requires a host,
  // but an unfinished supported address does not have the wrong scheme.
  const n7NotRtsp =
    !!urlValue.trim() && !isRtsp(toProtocol(urlValue.trim().match(/^([^:]+):\/\//)?.[1]));
  const validateN7Address = () => {
    if (n7NotRtsp) {
      openN7(ALERT_VARIANT.NOT_RTSP);
    }
  };

  // One-shot latch for the RTSP_MANUAL_URL_EDITED edge. Evaluated inside the
  // change handler and NEVER in an effect: main.tsx wraps the app in
  // <StrictMode>, which double-invokes effects in dev, so an effect-based emit
  // would double-fire (and be untestable under the StrictMode-less jest renderer).
  const manualUrlEditedRef = useRef(false);

  // Stream-URL onChange. The field is credential-FREE by construction — the real
  // creds live in the dedicated
  // DeviceUsername/DevicePassword fields and are re-folded at commit
  // (effectiveUrl above). But a user may PASTE a full `rtsp://user:pass@host`
  // URL straight into this field. If we just stored it verbatim, a later
  // structured change (overwrite-on-select) or a creds-empty fold would silently
  // DROP those pasted creds and the committed URL would become anonymous →
  // connectivity fails with no indication. So when the pasted value carries
  // credentials AND the dedicated cred state is still empty, HOIST the parsed
  // user/pass into the username/password state and keep the field itself
  // credential-free (stripRtspCredentials). We never override creds the user
  // already typed in the dedicated fields.
  const handleStreamUrlChange = (next: string) => {
    n7Attempt.current = undefined;
    closeN7(ALERT_CLOSE_REASON.DISMISS);
    const parsed = parseUri(next);
    const pastedUser = toString(parsed?.username);
    const pastedPass = toString(parsed?.password);

    // The value actually STORED — credential-free whenever the input carried
    // credentials. The analytics edge below compares THIS, not the raw `next`:
    // pasting `rtsp://user:pass@<the composed host/path>` is not a divergence.
    let stored = next;

    if (parsed?.host && (pastedUser || pastedPass)) {
      // Only hoist into an EMPTY dedicated field — never clobber a user-typed
      // credential. (Hoist user and pass independently.)
      if (pastedUser && !username) {
        setUsername(pastedUser);
      }
      if (pastedPass && !password) {
        setPassword(pastedPass);
      }
      // Keep the editable field credential-free regardless of whether we
      // hoisted (the dedicated fields are the single source of truth for creds).
      stored = stripRtspCredentials(next) ?? next;
    }

    setManualUrl(stored);

    // The pencil click that used to emit this event no longer exists, so
    // re-anchor it on the divergence EDGE — once per
    // false→true transition, re-armed when the value returns to the composed
    // URL. Brand/model context only; the URL itself is NEVER attached (it can
    // embed `rtsp://user:pass@host`).
    if (stored === composedDisplayUrl) {
      manualUrlEditedRef.current = false;
    } else if (!manualUrlEditedRef.current) {
      manualUrlEditedRef.current = true;
      track(EVENT_NAMES.RTSP_MANUAL_URL_EDITED, EVENT_CATEGORIES.VIDEO_SOURCES, {
        // Canonical device-cohort keys; bare brand/model are off-convention and
        // dropped (prod) / thrown (dev/test) by track().
        ...(selectedBrand?.name ? { brand_name: selectedBrand.name } : {}),
        ...(template?.model?.name ? { model_name: template.model.name } : {})
      });
    }
  };

  // Overwrite-on-select: a structured change drops the override so the field
  // re-mirrors the freshly composed URL (selection wins over a hand-typed URL).
  // Automatic stream defaults may arrive after the user checked a manual URL.
  // Only an explicit stream selection may discard that override.
  //
  // `username`/`password` are DELIBERATELY NOT dependencies (engineer-approved
  // narrowing). The displayed value is credential-free, so a credential
  // keystroke cannot change what this effect would revert TO: for a guided user
  // the revert is a provable no-op, and for a user who typed a URL it is pure
  // destruction — fired from a field rendered ABOVE the URL row (off-gaze) on
  // keystroke 1. TO REVERSE: re-add `username, password` here AND restore the
  // `suppressNextRevertRef` one-shot guard (setter in the credential-hoist
  // branch of handleStreamUrlChange, early-return consumer at the top of this
  // effect) — both together, or the flag is never consumed and the next genuine
  // IP/brand/model change swallows its own revert.
  useUpdateEffect(() => {
    setManualUrl(undefined);
    // Back on the composed URL — re-arm the analytics divergence edge.
    manualUrlEditedRef.current = false;
  }, [ipAddress, selection.brandId, selection.modelId]);

  const handleStreamSelection = (id: string) => {
    setSelectedId(id);
    setManualUrl(undefined);
    manualUrlEditedRef.current = false;
  };

  // Validity of the Stream URL (for the Input error variant): a parseable,
  // RTSP-family URI WITH a host. Mirrors isCheckConnectivityEnabled's URI test.
  // Keyed on `urlValue`, NOT `manualUrl`: keyed on the override alone, a normal
  // composed URL (no override → `undefined`) would be judged invalid and paint
  // the field red on every mount.
  const streamUri = useMemo(() => (urlValue ? parseUri(urlValue) : undefined), [urlValue]);
  const isUrlValid = !!streamUri && isRtsp(toProtocol(streamUri.protocol)) && !!streamUri.host;

  /*
   * Reset the candidate's committed video config whenever the inputs that feed
   * the effective URL change, so the user must re-run Check Connectivity (the
   * device must be re-pushed to the agent). Mirrors the original clear-on-edit
   * behavior; only this clear + the Check-Connectivity write touch
   * candidate.video.
   */
  const resetVideoConfiguration = () => {
    writeOnVideoStep((draft) => {
      draft.candidate.video = { protocol: Protocol.RTSP };
      // Also clear the Video-panel ready flag. The footer gates Continue on
      // `video.isReady`; clearing only `candidate.video` here left `isReady`
      // stuck true from a prior successful Check, so editing the inputs let the
      // user proceed on a config that had NOT been re-verified. Both must reset
      // together.
      draft.video.isReady = false;
    });
  };

  useUpdateEffect(() => {
    closeN7(ALERT_CLOSE_REASON.DISMISS);
    n7Attempt.current = undefined;
    resetVideoConfiguration();
    setLastCheckedUrl(undefined);
    // A changed URL is eligible for its own automatic attempt.
    setAutoCheckedUrl(undefined);
    // Reset the empty-creds-probe machine on the same effective-URL change that
    // clears candidate.video, so a stale probe verdict can never leak across
    // inputs. RESET bumps the probe generation, so the generation-scoped phase
    // timer (armed by the effect above) is cancelled by its own cleanup and any
    // pending timeout is invalidated.
    dispatchProbe({ type: 'RESET' });
    // Clear the probe-owned {HTTP, port} candidate.control on RESET too — DONE
    // was the only path that cleared it, so a reset mid-probe (or a cred edit
    // that clears both fields) left the synthetic control config in the store,
    // where it leaked to the Control panel / commit. In this wizard the probe is
    // the ONLY writer of a non-undefined candidate.control (syncSelection only
    // sets it undefined), so clearing it here is safe.
    writeOnVideoStep((draft) => {
      draft.candidate.control = undefined;
    });
  }, [effectiveUrl]);

  // Relocated manufacturer-panel writes: keep candidate.modelId +
  // manufacturer.config coherent for the downstream Control panel / commit.
  //
  // When brand/model selection was relocated here off the (removed)
  // manufacturer panel, the panel's `manufacturer.defaultName`
  // write was dropped. `defaultName` is the committed device's fallback name
  // (wiring.ts handleCommit: `defaults({}, candidate, { name: defaultName })`),
  // so without it the tile rendered as "unknown" / blank even though a model
  // was selected. Restore the panel's `getDefaultName({ brands, models,
  // selection })` derivation so the committed device carries the selected
  // brand+model nickname. Recomputes alongside `config` so any
  // brand/model/registry-fetch change keeps it coherent.
  useEffect(() => {
    writeOnVideoStep((draft) => {
      draft.manufacturer.config = template;
      draft.manufacturer.isReady = !!template;
      draft.manufacturer.defaultName = getDefaultName({ brands, models, selection });
    });
  }, [template, brands, models, selection]);

  // ONVIF/known-model prefill: when a model id was prefilled (candidate.modelId)
  // but no brand is selected yet, seed the brand from the resolved template so
  // the Brand dropdown reflects the prefilled device (mirrors the old
  // manufacturer/details.tsx getInitialSelection).
  //
  // ENG-4832 correction: a model written from outside (the not-listed flow,
  // followed below) may have kept the previous brand because its own brand was
  // not cached yet. Once the template resolves FOR THAT model, realign the
  // brand with it. Gated on `template.model.id === selection.modelId` so a
  // template still resolving after a user's brand change (which clears the
  // model) can never revert that choice. Mirrors manufacturer/details.tsx.
  useEffect(() => {
    const brandId = template?.model?.brandId;
    if (!brandId) {
      return;
    }

    const mismatch = template?.model?.id === selection.modelId && brandId !== selection.brandId;
    if (!selection.brandId || mismatch) {
      setSelection((prev) => ({ ...prev, brandId }));
    }
  }, [template, selection.brandId, selection.modelId]);

  // Brand-level fingerprint seed: when the user arrived via a brand-only
  // discovery match (panel.tsx set source.seededBrandId), pre-seed
  // the Brand dropdown so the RTSP Helper opens with the brand selected and
  // only the model left to pick. Mirrors the template-derived brand seed
  // above; guarded on `!selection.brandId` so it never clobbers a user choice
  // and runs at most once (the seed is sticky in the store for the session).
  useEffect(() => {
    if (seed.seededBrandId && !selection.brandId) {
      setSelection((prev) => ({ ...prev, brandId: seed.seededBrandId }));
    }
  }, [seed.seededBrandId, selection.brandId]);

  // ENG-4832: the not-listed flow (mounted by the scaffold) writes
  // `candidate.modelId` itself — Continue → generic Other/Other model, "Did you
  // mean" → the picked model. Follow it so the local selection, and with it
  // `useTemplate` / `manufacturer.config`, describe the model the store saves.
  //
  // The brand is looked up across EVERY cached collection (the depot registry),
  // not just the selected brand's: Continue's generic model lives under
  // Brand="Other" and a "Did you mean" pick may belong to another brand. When
  // the model is cached nowhere, keep the previous brand rather than clearing
  // it — an empty brand would let the brand-only discovery seed effect above
  // refire (`!selection.brandId`) and win over the template seed, pinning e.g.
  // Sony onto a Panasonic model; the template-brand effect above corrects the
  // kept brand once the model's template resolves. Mirrors
  // manufacturer/details.tsx. Per rule: think-in-systems (ai.knowledge/global).
  const storeModelId = useStore((µ) => µ.candidate.modelId);
  const markModelWritten = useExternalModelId(storeModelId, (modelId) => {
    setSelection((prev) => ({
      brandId: findBrandIdForModel(modelsByBrand, modelId) ?? prev.brandId,
      modelId
    }));
    // A value the user chose elsewhere is no longer what discovery detected.
    setChanged({ brand: true, model: true });
  });

  // ENG-4832 / ENG-4924 C3 inputs and the wizard's "My camera isn't listed"
  // action. C3 shows for every not-listed answer — "isn't listed" (C1) and a
  // not-yet-supported pick (C2) alike — and its "Change" reopens the flow on
  // the screen that produced the answer, with the typed text kept.
  const isNotListedTyped = useStore((µ) => isNotListedAnswer(µ.identification?.identifiedBy));
  const typedModel = useStore((µ) => µ.identification?.typedModel?.trim());
  const hasIdentification = useStore((µ) => !!µ.identification);
  const handleNotListed = useCallback(() => {
    producer((draft) => {
      if (draft.identification) reopenNotListed(draft);
    });
  }, [producer]);

  const setIdentifiedBy = useLatestFn((identifiedBy: IdentifiedBy) => {
    producer((draft) => {
      if (draft.identification) markIdentifiedBy(draft, identifiedBy);
    });
  });

  /**
   * Drop the pills of the fields the operator just changed. Returns whether a
   * detected value was replaced (ENG-4833: a replaced detection is reported as
   * a plain `searched`; the ticket has no separate bucket for it).
   */
  const clearDetected = useLatestFn((fields: (keyof Detected)[]): boolean => {
    const replaced = fields.some((field) => detected[field]);

    if (replaced) {
      setChanged((µ) => ({ ...µ, ...Object.fromEntries(fields.map((f) => [f, true])) }));
    }

    return replaced;
  });

  // ENG-4924: a "Not yet supported" model is never selected. Picking it — from
  // the model list or a combined "Brand · Model" row — opens C2 prefilled with
  // the picked names ("<Brand Model> isn't supported yet"); C2's Continue writes
  // the generic model through the not-listed flow, which the follower above
  // mirrors into the selection, and records `not_yet_supported_picked`.
  // Mirrors manufacturer/details.tsx; the slice owns the open state and the answer.
  // Per rule: think-in-systems (ai.knowledge/global).
  const openNotYetSupported = useLatestFn((model: M_Model) => {
    producer((draft) => {
      if (draft.identification) {
        openNotListed(
          draft,
          'c2',
          { brand: brands?.items?.[model.brandId]?.name, model: model.name },
          'not_yet_supported_pick'
        );
      }
    });
  });

  // ENG-4831 B5: the pickers' "My camera isn't listed" action hands over the
  // text the user already typed, so C1 opens prefilled instead of blank. The
  // model action also carries the selected brand's name — unless that is the
  // catch-all "Other" brand, which is not a brand the user meant to type.
  // Mirrors manufacturer/details.tsx; the slice applies the seed for both.
  // Per rule: think-in-systems (ai.knowledge/global).
  const seedBrandName = otherBrand ? undefined : selectedBrand?.name;
  const handleBrandNotListed = useCallback(
    (query: string) => {
      producer((draft) => {
        if (draft.identification) openNotListed(draft, 'c1', { brand: query });
      });
    },
    [producer]
  );
  const handleModelNotListed = useCallback(
    (query: string) => {
      producer((draft) => {
        if (draft.identification)
          openNotListed(draft, 'c1', { brand: seedBrandName, model: query });
      });
    },
    [producer, seedBrandName]
  );

  const syncSelection = useLatestFn((next: RequireAtLeastOne<Selection>) => {
    const nextSelection = { ...selection, ...next };

    if (shallowEqual(selection, nextSelection)) {
      return;
    }

    setSelection(nextSelection);
    // Our own write: must not come back as an "external" change.
    markModelWritten(nextSelection.modelId);

    writeOnVideoStep((draft) => {
      draft.candidate.modelId = nextSelection.modelId;
      // Clear stale control settings when the model changes (relocated from
      // manufacturer/details.tsx syncSelection).
      draft.candidate.control = undefined;
    });
  });

  const trackBrand = useLatestFn((brandId: M_Brand['id']) => {
    const brand = brands?.items?.[brandId];
    if (brand) {
      deviceLifecycleTracker.trackDeviceSelectBrand({
        brand_name: brand.name,
        device_brand_id: brandId.toString()
      });
    }
  });

  const trackModel = useLatestFn((model: M_Model, brandId: M_Brand['id'] | undefined) => {
    const brandName = brandId ? brands?.items?.[brandId]?.name : undefined;
    deviceLifecycleTracker.trackDeviceSelectModel({
      model_name: model.name,
      device_model_id: model.id.toString(),
      ...(brandName ? { brand_name: brandName } : {}),
      ...(brandId ? { device_brand_id: brandId.toString() } : {})
    });
  });

  const handleBrandChange = useLatestFn((brandId: M_Brand['id']) => {
    syncSelection({ brandId, modelId: undefined });
    trackBrand(brandId);

    // ENG-4833: a different brand also invalidates the detected model; replacing
    // a detection is a plain search (mirrors manufacturer/details.tsx).
    if (clearDetected(['brand', 'model'])) {
      setIdentifiedBy('searched');
    }
  });

  const handleModelChange = useLatestFn((modelId: M_Model['id']) => {
    const model = models?.items?.[modelId];

    // ENG-4924: a not-yet-supported row opens C2 instead of becoming the selection.
    if (model?.notYetSupported) {
      openNotYetSupported(model);

      return;
    }

    syncSelection({ modelId });

    if (model) {
      trackModel(model, selection.brandId);
      // ENG-4833: read the detected state before the pick clears it — a brand
      // still detected with no detected model is `brand_detected_then_searched`.
      const brandOnlyDetected = detected.brand && !detected.model;
      clearDetected(['model']);
      setIdentifiedBy(getPickedIdentifiedBy(brandOnlyDetected));
    }
  });

  // ENG-4831 B3: a combined "Brand · Model" row commits both ids in one update
  // (mirrors manufacturer/details.tsx `handleCombinedChange`).
  const handleCombinedChange = useLatestFn((model: M_Model) => {
    // ENG-4924: a not-yet-supported combined row opens C2 like a model-list pick.
    if (model.notYetSupported) {
      openNotYetSupported(model);

      return;
    }

    syncSelection({ brandId: model.brandId, modelId: model.id });
    trackBrand(model.brandId);
    trackModel(model, model.brandId);

    const fields: (keyof Detected)[] =
      model.brandId === selection.brandId ? ['model'] : ['brand', 'model'];
    // ENG-4833: only a pick inside the detected brand counts as
    // brand_detected_then_searched; a different brand is a plain search.
    const brandOnlyDetected = fields.length === 1 && detected.brand && !detected.model;
    clearDetected(fields);
    setIdentifiedBy(getPickedIdentifiedBy(brandOnlyDetected));
  });

  // Brand="Other" auto-selects the brand's generic ("Other") model so its
  // template resolves (mirrors manufacturer/details.tsx). The Model selector is
  // hidden in this case; the generic model id still drives useTemplate.
  useEffect(() => {
    if (!otherBrand) {
      return;
    }

    if (genericModelId && genericModelId !== selection.modelId) {
      syncSelection({ modelId: genericModelId });
    }
  }, [otherBrand, genericModelId, selection.modelId, syncSelection]);

  const memoizedBrandSelection = useMemo(
    () => toAccessor(selection.brandId, handleBrandChange),
    [selection.brandId, handleBrandChange]
  );

  const memoizedModelSelection = useMemo(
    () => toAccessor(selection.modelId as Opaque<string, '__deviceModelId'>, handleModelChange),
    [selection.modelId, handleModelChange]
  );

  const checkEnabled = isCheckConnectivityEnabled(effectiveUrl, lastCheckedUrl);

  // An automatic attempt for the current URL that is live and that no Check
  // Connectivity click has adopted yet.
  const autoAttemptLive =
    hasVideo &&
    !!effectiveUrl &&
    autoCheckedUrl === effectiveUrl &&
    lastCheckedUrl !== effectiveUrl;

  // Both the button and the automatic attempt commit through here: writing
  // candidate.video makes the mediator register the transient `tmp:` preview,
  // and every input change resets candidate.video first, so a re-attempt always
  // removes the previous preview (and its urxCache entry) before adding the next.
  const runConnectivityCheck = useLatestFn((source: 'manual' | 'auto') => {
    if (showN7 && n7NotRtsp) {
      validateN7Address();
      return;
    }
    if (!effectiveUrl || !checkEnabled) {
      return;
    }
    n7Attempt.current = { url: effectiveUrl, agentId };

    // A click on the URL the automatic attempt is already connecting to adopts
    // that attempt: re-writing the same candidate would not reach the mediator,
    // and clearing the failure latch would hide its outcome. It is the same
    // attempt, which already reported its CONFIRM, so the click emits nothing.
    if (source === 'manual' && autoAttemptLive) {
      setLastCheckedUrl(effectiveUrl);
      return;
    }

    // Re-arm the sticky connection error: a fresh check clears any prior
    // connection failure AND cancels a pending grace timer before re-attempting.
    clearConnectionFailure();

    // Empty-creds probe: when the user entered credentials that were NOT
    // prefilled by discovery, run the discriminator —
    // probe FIRST with EMPTY creds to learn whether the server actually enforces
    // auth. Anonymous (no creds) and prefilled-creds flows keep the existing
    // single-write behavior (nothing to validate / already validated).
    const runProbe = hasEnteredCreds && !credsPrefilled;

    if (runProbe) {
      writeOnVideoStep((draft) => {
        // Phase 1: probe the video plane with NO credentials (URX
        // rtsp://host:554/path). stripRtspCredentials yields the credential-free
        // URX from the effective (real-creds) URL.
        draft.candidate.video = toConfiguration(
          parseUri(stripRtspCredentials(effectiveUrl) ?? effectiveUrl)
        );
        // When a real model is selected there is an HTTP control module to
        // probe: force a no-creds {HTTP, host, port} control transient
        // (replicating the one-click flow, panel.tsx). Use HttpDefaults.port
        // (80), NOT port:0 — a `port:0` serializes to a PORT-LESS URX
        // (models/net/utils.ts drops :0), which makes the encoder HTTP builder
        // ERROR so controlAuthenticationStatus never arrives and the probe waits
        // the full 15s.
        if (modelSelected && parsedIp.host) {
          draft.candidate.control = {
            protocol: ControlProtocol.HTTP,
            host: parsedIp.host,
            port: HttpDefaults.port
          };
        }
      });
      // START enters PROBE_EMPTY (a new generation); the generation-scoped timer
      // effect arms Phase 1's 15s timeout on that entry.
      dispatchProbe({ type: 'START' });
    } else {
      writeOnVideoStep((draft) => {
        // Existing single candidate.video writer: parse the effective URL (the
        // manual override when editing, else the composed URL — which already
        // carries any credentials) into a configuration. toConfiguration applies
        // the RtspDefaults port (554) and preserves query strings.
        draft.candidate.video = toConfiguration(parseUri(effectiveUrl));
      });
    }

    // CONFIRM marks the start of every connection attempt, clicked or automatic:
    // exactly once, same payload, ahead of the attempt's OUTCOME. Per rule:
    // preserve-historical-telemetry-during-renames (ai.knowledge/platform) — the
    // funnel's once-per-attempt CONFIRM keeps its meaning.
    // Emitted AFTER the producer commits so this analytics side effect never
    // fires from inside the immer recipe (where a throwing draft mutation would
    // roll back state but leave the event already sent). The Stream URL itself
    // is NEVER attached (credential-bearing). brand_name/model_name are the
    // canonical device-cohort keys; bare brand/model are off-convention and
    // would be dropped (prod) or throw (dev/test) in assertCanonicalDeviceProps.
    track(EVENT_NAMES.CONFIRM_RTSP_VIDEO_SETTINGS, EVENT_CATEGORIES.VIDEO_SOURCES, {
      hasManualUrl,
      ...(selectedBrand?.name ? { brand_name: selectedBrand.name } : {}),
      ...(template?.model?.name ? { model_name: template.model.name } : {})
    });

    // A click marks the URL checked; an automatic attempt stays adoptable by a
    // click until it settles.
    if (source === 'manual') {
      setLastCheckedUrl(effectiveUrl);
    } else {
      setAutoCheckedUrl(effectiveUrl);
    }
  });

  // ENG-4927: identify at address. "Unidentified" means no brand picked, or the
  // catch-all Brand="Other" (whose generic model is auto-selected above, so
  // `modelId` alone cannot tell), and no not-listed answer either. The catch-all
  // Other is treated as unidentified and IS replaced by a match; a specific
  // brand/model the operator picked is never overridden — the gate is re-read on
  // the latest render via `useLatestFn`, so a pick made while the Bridge probes
  // wins over the late answer.
  const identifyAtHost = useIdentifyAtHost();
  const cameraUnidentified = !isNotListedTyped && (!selection.brandId || otherBrand);

  const applyIdentifiedRow = useLatestFn((row: IdentifiedDiscovery) => {
    // Re-read on the latest render: a brand the operator picked while the
    // Bridge was probing wins over the late answer.
    if (!cameraUnidentified) {
      return;
    }

    // Same writer as a combined "Brand · Model" pick: local selection,
    // candidate.modelId and the stale-control clear in one step. Nothing here
    // writes the IP or the Stream URL; the model change flows through the
    // existing overwrite-on-select rule like any pick, so the URL re-composes
    // from the typed IP plus the identified model's stream path.
    //
    // A model-level match carries no brandId. Writing it as undefined clears
    // any catch-all "Other" brand, and the template brand seed above then sets
    // the model's own brand once its template resolves.
    syncSelection({ brandId: row.brandId, modelId: row.modelId });
    setEntry({ identifiedBy: 'detected', modelId: row.modelId });
    setChanged({});
    setIdentifiedBy('detected');
  });

  const identifyUnpickedCamera = useLatestFn(() => {
    // `parsedIp.valid` is the field's own IP/hostname syntax check; the clean
    // host (no `:port`) is what the Bridge probes and what its row carries.
    if (!cameraUnidentified || !parsedIp.valid) {
      return;
    }

    // Timeout and cancel resolve `undefined` — nothing changes (never guess).
    void identifyAtHost(agentId, parsedIp.host).then((row) => {
      if (row) {
        applyIdentifiedRow(row);
      }
    });
  });

  // ENG-4940: a discovered camera that reaches this step (an ONVIF or RTSP row
  // with no model) already carries its address, so identify it as the step
  // opens instead of waiting for Check Connectivity. Same gate and "a pick
  // wins" rule as the button; one probe of that one host. A brand-only
  // discovery seed is skipped: its brand already counts as picked under the
  // gate, so an answer could never apply.
  useEffect(() => {
    if (!seed.modelId && !seed.seededBrandId && (seed.host ?? seed.seededHost)) {
      identifyUnpickedCamera();
    }
  }, []);

  const handleCheckConnectivity = useLatestFn(() => {
    // A Check Connectivity press counts as touching the IP field (blank-IP text).
    setIpTouched(true);
    runConnectivityCheck('manual');
    // Concurrent with the check — the identify wait never delays it.
    identifyUnpickedCamera();
  });

  // Drop the current attempt: clearing candidate.video makes the mediator remove
  // its preview; the last-checked URL and the probe machine reset so the next
  // check starts from scratch, and the probe-owned control is cleared.
  //
  // The probe reset matters: a machine left in DONE stays DONE — the next Check
  // writes the empty-creds candidate, START is a no-op (only fires from IDLE),
  // the anonymous stream plays, and the user can Continue with EMPTY creds.
  const abandonAttempt = () => {
    resetVideoConfiguration();
    setLastCheckedUrl(undefined);
    // RESET bumps the probe generation; the generation-scoped phase timer is
    // cancelled by its own cleanup and any pending timeout invalidated.
    dispatchProbe({ type: 'RESET' });
    writeOnVideoStep((draft) => {
      draft.candidate.control = undefined;
    });
  };

  // Bridge/agent change forces a re-check on the new bridge — automatically for a
  // URL without a login, otherwise on the next password commit or via the
  // re-enabled button.
  const handleAgentChange = (_agentId: string) => {
    n7Attempt.current = undefined;
    closeN7(ALERT_CLOSE_REASON.DISMISS);
    abandonAttempt();
    setAutoCheckedUrl(undefined);
  };

  // Cancel the attempt in flight. The URL is remembered as auto-attempted so the
  // automatic attempt does not immediately restart it; Check Connectivity retries.
  const handleCancelAttempt = useLatestFn(() => {
    closeN7(ALERT_CLOSE_REASON.DISMISS);
    abandonAttempt();
    clearConnectionFailure();
    setAutoCheckedUrl(effectiveUrl);
  });

  // Automatic attempt: once nothing is previewing and the URL is complete (see
  // isAutoAttemptUrl), connect without waiting for a click; entered credentials
  // run the empty-credential check as a click does. Keyed on the effective URL,
  // so the check's own anonymous preview never starts another attempt, and each
  // URL is attempted at most once. A URL without a login is attempted once
  // typing settles; one with a login waits for the password to be committed
  // (blur or Enter), because a timer would try half-typed passwords and cameras
  // lock an account after repeated failed logins.
  const AUTO_ATTEMPT_DEBOUNCE_MS = 1500;
  const autoAttemptEligible =
    !hasVideo && checkEnabled && isAutoAttemptUrl(effectiveUrl) && autoCheckedUrl !== effectiveUrl;
  const attemptCarriesLogin = urxHasCredentials(effectiveUrl);
  const autoAttemptPending = autoAttemptEligible && !attemptCarriesLogin;
  const handlePasswordCommit = useLatestFn(() => {
    if (autoAttemptEligible && attemptCarriesLogin) {
      runConnectivityCheck('auto');
    }
  });
  // Read through a ref so a re-render never restarts the debounce.
  const runConnectivityCheckRef = useRef(runConnectivityCheck);
  runConnectivityCheckRef.current = runConnectivityCheck;

  useEffect(() => {
    if (!autoAttemptPending) {
      return undefined;
    }
    const timer = setTimeout(() => {
      runConnectivityCheckRef.current('auto');
    }, AUTO_ATTEMPT_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [autoAttemptPending, effectiveUrl]);

  const authMessage = authStatusMessage(authStatus, hasEnteredCreds);

  // Emit a single connectivity-OUTCOME event once the Check-Connectivity attempt
  // resolves, distinguishing auth-failed
  // (credentials rejected) from connection-failed (host unreachable / stream
  // error) — today both collapse into a generic connect error. Deduped per
  // (deviceId, outcome) so the async status churn (CONNECTING↔FAILED) fires
  // exactly one event per resolved outcome. Brand/model context only — NO
  // credential-bearing URL.
  const lastOutcomeRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    // Phase-1 suppression: the empty-creds probe's own connect/auth churn is not
    // a user connectivity attempt. Suppress by verdict PROVENANCE, not just
    // phase — a plain `phase === PROBE_EMPTY` guard would let a phantom event
    // fire on the
    // `phase === PROBE_EMPTY` guard let a phantom event fire on the
    // PROBE_EMPTY→next transition, where phase already moved on but authStatus is
    // still the STALE empty-probe verdict for the empty transient's deviceId.
    // Suppress while probing, AND while the verdict still comes from the empty
    // (no-creds) transient though an empty-creds probe applies. Emit only from a
    // real-creds probe (URX carries creds) or when no probe applies (anonymous /
    // prefilled single check).
    if (probe.phase === Phase.PROBE_EMPTY || probe.phase === Phase.PROBE_REAL) {
      return;
    }
    const emptyCredsProbeApplies = hasEnteredCreds && !credsPrefilled;
    if (emptyCredsProbeApplies && !urxHasCredentials(videoUrx)) {
      return;
    }

    const authFailed =
      authStatus === AuthenticationStatus.AUTHENTICATION_STATUS_INVALID ||
      authStatus === AuthenticationStatus.AUTHENTICATION_STATUS_ERROR;
    const connected = videoConnStatus === StatusDeviceConnectionStatus.CONNECTION_STATUS_CONNECTED;

    // Auth dominates: a credential rejection is the actionable cause even if
    // the transport also reports FAILED. Then connection-failed (the latched
    // grace-debounced failure). Then success. Anything else → no outcome yet.
    const outcome: 'auth_failed' | 'connection_failed' | 'success' | undefined = authFailed
      ? 'auth_failed'
      : connectionFailed
        ? 'connection_failed'
        : connected
          ? 'success'
          : undefined;

    if (!deviceId || outcome === undefined) {
      return;
    }
    const dedupeKey = `${deviceId}:${outcome}`;
    if (lastOutcomeRef.current === dedupeKey) {
      return;
    }
    lastOutcomeRef.current = dedupeKey;

    track(EVENT_NAMES.RTSP_CONNECTIVITY_OUTCOME, EVENT_CATEGORIES.VIDEO_SOURCES, {
      connectivityOutcome: outcome,
      hasManualUrl,
      // Canonical device-cohort keys; bare brand/model are off-convention and
      // dropped (prod) / thrown (dev/test) by track().
      ...(selectedBrand?.name ? { brand_name: selectedBrand.name } : {}),
      ...(template?.model?.name ? { model_name: template.model.name } : {})
    });
    // `hasManualUrl` (a boolean) is the dep, NEVER `urlValue` — the string would
    // re-run this effect on every keystroke.
  }, [
    authStatus,
    videoConnStatus,
    connectionFailed,
    deviceId,
    hasManualUrl,
    selectedBrand?.name,
    template?.model?.name,
    track,
    probe.phase,
    videoUrx,
    hasEnteredCreds,
    credsPrefilled
  ]);

  // --- Empty-creds-probe orchestration -------------------------------------
  //
  // The pure machine (auth-probe.ts) decides transitions; these effects perform
  // the store side effects and feed the transient verdicts back in. All values
  // the probe consumes are read from refs so the orchestration effects can
  // depend on `probe.phase` alone without stale-closure reads or re-running on
  // every keystroke.
  const effectiveUrlRef = useRef(effectiveUrl);
  effectiveUrlRef.current = effectiveUrl;
  const parsedHostRef = useRef(parsedIp.host);
  parsedHostRef.current = parsedIp.host;
  const credentialsRef = useRef(credentials);
  credentialsRef.current = credentials;
  const modelSelectedRef = useRef(modelSelected);
  modelSelectedRef.current = modelSelected;
  // The current probe phase, read by the unmount cleanup (which runs once with a
  // stale closure) to decide whether an unmount happened mid-probe.
  const probePhaseRef = useRef(probe.phase);
  probePhaseRef.current = probe.phase;

  // Phase-1 classifier: watch the empty-creds video + control verdicts and, once
  // BOTH aspects are terminal (joint wait) with a preview device present — or the
  // phase timed out — feed the machine an EMPTY_VERDICT.
  useEffect(() => {
    if (probe.phase !== Phase.PROBE_EMPTY) {
      return;
    }
    // Correlate to the EMPTY-probe transients. Their URX carries NO userinfo, so
    // a verdict read from a URX that DOES carry credentials belongs to a stale
    // real-creds transient still being torn down — treat it as pending, never as
    // the empty probe's evidence. `deviceId` alone is not enough (it may be the
    // lingering previous transient's id).
    const videoIsEmptyProbe = !!deviceId && !urxHasCredentials(videoUrx);
    const controlIsEmptyProbe = !urxHasCredentials(controlUrx);
    const effAuth = videoIsEmptyProbe ? authStatus : undefined;
    const effControl = controlIsEmptyProbe ? controlAuthStatus : undefined;
    const effControlErr = controlIsEmptyProbe ? controlErrorAuthFail : false;

    const rtsp = classifyRtspEmpty(effAuth, { connectionFailed, timedOut: phaseTimedOut });
    const http = classifyHttpEmpty(effControl, {
      modelSelected,
      timedOut: phaseTimedOut,
      errorAuthFail: effControlErr
    });
    const bothTerminal = rtsp !== undefined && http !== undefined;
    // Advance when a FRESH empty-probe device exists AND both aspects are
    // terminal, OR the timeout elapsed (so a device that never spins up cannot
    // stall us). On timeout the classifiers already return terminal verdicts, so
    // the `??` fallbacks are only for the type-checker.
    if ((videoIsEmptyProbe && bothTerminal) || phaseTimedOut) {
      dispatchProbe({
        type: 'EMPTY_VERDICT',
        rtsp: rtsp ?? 'RTSP_AMBIGUOUS',
        http: http ?? (modelSelected ? 'HTTP_UNKNOWN' : 'HTTP_NA')
      });
    }
  }, [
    probe.phase,
    authStatus,
    videoUrx,
    controlAuthStatus,
    controlUrx,
    controlErrorAuthFail,
    connectionFailed,
    phaseTimedOut,
    deviceId,
    modelSelected
  ]);

  // Phase-2 classifier: once the real-creds VIDEO verdict is terminal (and the
  // control verdict too when Phase 2 re-probes HTTP, or the timeout fires), feed
  // the machine a REAL_VERDICT. decidePhase2 gates Continue on VIDEO only.
  useEffect(() => {
    if (probe.phase !== Phase.PROBE_REAL) {
      return;
    }
    // Phase 2 accepts ONLY the real-creds transients — those whose URX carries
    // userinfo. On entry to PROBE_REAL the reads STILL belong to the Phase-1
    // empty transient (URX no creds, mediator swap is async), so gate on
    // freshness and keep waiting until the creds-bearing transient arrives.
    // Without this the empty-probe verdict is consumed as the "real" result and
    // Phase 2 never actually validates the real creds.
    const videoIsRealProbe = !!deviceId && urxHasCredentials(videoUrx);
    const controlIsRealProbe = urxHasCredentials(controlUrx);
    const effAuth = videoIsRealProbe ? authStatus : undefined;
    const effControl = controlIsRealProbe ? controlAuthStatus : undefined;
    const effControlErr = controlIsRealProbe ? controlErrorAuthFail : false;

    const videoTerminal =
      effAuth === AuthenticationStatus.AUTHENTICATION_STATUS_VALIDATED ||
      effAuth === AuthenticationStatus.AUTHENTICATION_STATUS_INVALID;
    const needsHttp = probe.http ? phase2NeedsHttp(probe.http) : false;
    const controlTerminal =
      effControl === AuthenticationStatus.AUTHENTICATION_STATUS_VALIDATED ||
      effControl === AuthenticationStatus.AUTHENTICATION_STATUS_INVALID ||
      effControl === AuthenticationStatus.AUTHENTICATION_STATUS_UNSUPPORTED ||
      effControl === AuthenticationStatus.AUTHENTICATION_STATUS_ERROR ||
      effControlErr;
    // `phaseTimedOut` forces a terminal decision even when the fresh video
    // verdict never lands (decidePhase2 resolves undefined+timedOut → error), so
    // PROBE_REAL can't hang forever with DONE cleanup never running.
    if ((videoTerminal && (!needsHttp || controlTerminal)) || phaseTimedOut) {
      dispatchProbe({
        type: 'REAL_VERDICT',
        video: effAuth,
        control: effControl,
        controlErrorAuthFail: effControlErr,
        timedOut: phaseTimedOut
      });
    }
  }, [
    probe.phase,
    probe.http,
    authStatus,
    videoUrx,
    controlAuthStatus,
    controlUrx,
    controlErrorAuthFail,
    phaseTimedOut,
    deviceId
  ]);

  // Phase-entry side effects: run exactly once on the transition INTO each phase
  // (guarded by enteredPhaseRef). PROBE_REAL writes the real-creds candidate;
  // DONE performs the final real-creds write + control cleanup.
  useEffect(() => {
    if (enteredPhaseRef.current === probe.phase) {
      return;
    }
    enteredPhaseRef.current = probe.phase;

    if (probe.phase === Phase.PROBE_REAL) {
      // Phase 2: re-probe with the REAL credentials. The video plane always
      // re-probes; the control plane re-probes only when Phase 1 left the HTTP
      // question open (auth-required or unknown).
      const reprobeHttp = modelSelectedRef.current && phase2NeedsHttp(probe.http ?? 'HTTP_NA');
      writeOnVideoStep((draft) => {
        draft.candidate.video = toConfiguration(parseUri(effectiveUrlRef.current));
        if (reprobeHttp && parsedHostRef.current) {
          draft.candidate.control = {
            protocol: ControlProtocol.HTTP,
            host: parsedHostRef.current,
            // HttpDefaults.port (80), not 0 — a port-less URX breaks the encoder
            // HTTP builder so the control verdict never arrives.
            port: HttpDefaults.port,
            credentials: {
              username: credentialsRef.current.user,
              password: credentialsRef.current.pass
            }
          };
        }
      });
      // Phase 2 entry re-arms a fresh, generation-scoped timeout via the timer
      // effect above — no imperative arm needed here.
    } else if (probe.phase === Phase.DONE) {
      writeOnVideoStep((draft) => {
        // CRITICAL correctness gate: candidate.video MUST carry the REAL entered
        // credentials before Continue/commit — even on the warning path where the
        // live transient used the empty creds. Adding a device without the user's
        // creds is a regression, so fold the real creds back in here.
        draft.candidate.video = toConfiguration(parseUri(effectiveUrlRef.current));
        // Clean the probe's control aspect so the {HTTP} transient never leaks
        // into the Control panel or the commit.
        draft.candidate.control = undefined;
      });
      // The generation-scoped timer effect clears its timer when the phase leaves
      // PROBE_* — no imperative clear needed here.
    }
  }, [probe.phase, probe.http]);

  // Expose the mid-probe signal as a store flag rather than overwriting
  // panel.tsx's computed video.isReady. `probing` is true only while the machine
  // is actively probing (PROBE_EMPTY/PROBE_REAL) and false at DONE/IDLE;
  // panel.tsx folds `!probing` into its OWN isReady and stays the sole writer. So
  // the credential-FREE empty-probe stream reaching PLAYING can never enable
  // Continue, yet Continue re-enables the instant the probe finishes — which a
  // direct isReady overwrite could not, since panel only re-writes isReady when
  // its own computed value changes.
  //
  // Written on every step, unlike writeOnVideoStep: only the Video step reads the flag, and
  // a write skipped during the exit fade could leave it set after the helper unmounts.
  useEffect(() => {
    const probing = probe.phase === Phase.PROBE_EMPTY || probe.phase === Phase.PROBE_REAL;
    producer((draft) => {
      draft.video.probing = probing;
    });
  }, [probe.phase]);

  // Back/unmount MID-PROBE must not leak the probe's synthetic state. The probe
  // writes a {HTTP} candidate.control and folds the user's REAL creds into
  // candidate.video during PROBE_REAL; if the user navigates away before DONE
  // both persist, and remount reads those creds off candidate.video, treats them
  // as a validated prefill, and SKIPS the probe. On unmount while still probing,
  // clear the probe-owned control and revert candidate.video to its
  // credential-FREE projection so remount re-runs the discriminator. After Back the
  // Source step's rebuild from the row replaces both instead. The phase is
  // read from a ref because this cleanup runs once at unmount; gating on a
  // mid-probe phase leaves the legitimate IDLE ONVIF/known-model seed (and the
  // committed DONE candidate) untouched.
  useEffect(
    () => () => {
      if (
        probePhaseRef.current !== Phase.PROBE_EMPTY &&
        probePhaseRef.current !== Phase.PROBE_REAL
      ) {
        return;
      }
      const url = effectiveUrlRef.current;
      const stripped = url ? (stripRtspCredentials(url) ?? url) : undefined;
      const base = stripped ? toConfiguration(parseUri(stripped)) : { protocol: Protocol.RTSP };
      producer((draft) => {
        // The probing-flag effect above has NO cleanup, so its last mid-probe
        // write (probing=true) would persist after this unmount. No consumer ever
        // recomputes `probing`, so it never self-heals on the non-RTSP path:
        // panel.tsx folds `playing && !video.probing` for the whole Video step,
        // and a stranded `true` permanently gates NDI/Local Continue. Reset it
        // here, in the same mid-probe-gated cleanup and on any step, so leaving
        // before DONE fully clears probe state.
        draft.video.probing = false;
        // Candidate writes only on the Video step (see writeOnVideoStep): after
        // Back the Source step owns the candidate.
        if (draft.panel !== Panel.Video) return;
        // The probe is the ONLY writer of a non-undefined candidate.control in
        // this wizard, so clearing it on a mid-probe unmount is safe.
        draft.candidate.control = undefined;
        draft.candidate.video = base;
      });
    },
    []
  );

  // Terminal probe messaging (consume-only). The warning applies whenever the
  // machine set a warnReason — i.e. no plane confirmed the entered creds; its
  // copy says why (the stream needs no login, control could not confirm them,
  // or neither plane gave a verdict). The control-auth error is advisory: it
  // shows when the control plane rejected the real creds but never gates
  // Continue (the footer gates on video.isReady only).
  const probeWarning =
    probe.phase === Phase.DONE ? probeWarningMessage(probe.warnReason) : undefined;
  const probeControlError =
    probe.phase === Phase.DONE && probe.controlError ? ControlAuthInvalidMessage : undefined;
  // A Phase-2 TIMEOUT ends with outcome 'error' but no auth status, so the
  // standard auth Callout never shows. Rather than a separate message, it is
  // folded into the unified "Connection failed" Callout below (same "couldn't
  // connect/verify" class). A 'rejected' error (real creds INVALID) is
  // deliberately excluded: its auth Callout already speaks, and two
  // contradicting messages would confuse.
  const probeTimedOut = probe.phase === Phase.DONE && probe.errorReason === 'timeout';

  // Only a TERMINAL auth verdict counts as a real reason to suppress the
  // "Connection failed" error: INVALID (credentials rejected) or ERROR
  // (couldn't verify). A transient "Validating…" is an in-progress state, not a
  // result — if it stalls while the connection has already failed, the failed
  // connection is the real error and must win, so VALIDATING is excluded here.
  const authIsTerminal =
    authStatus === AuthenticationStatus.AUTHENTICATION_STATUS_INVALID ||
    authStatus === AuthenticationStatus.AUTHENTICATION_STATUS_ERROR;

  // True when one of the two 'Authentication'-titled Callouts below is actually
  // rendered with a TERMINAL verdict — mirrors their EXACT render conditions.
  // When such an Authentication Callout is shown it already explains the real
  // cause of the failure, so the generic "Connection failed" error is redundant
  // and is suppressed (it would otherwise appear alongside "Authentication
  // failed" on a credential rejection). A still-validating status never
  // suppresses "Connection failed".
  const authSettled =
    authIsTerminal && probe.phase !== Phase.PROBE_EMPTY && probe.phase !== Phase.PROBE_REAL;
  const authCalloutVisible = authSettled || !!probeWarning;

  // An attempt for the current URL ends when the stream plays, the FAILED latch
  // or the CONNECTING backstop (the defined timeout) fires, the real-creds probe
  // times out, the commit fails, or a terminal auth verdict lands.
  const attemptSettled =
    videoIsPlaying || connectionFailed || probeTimedOut || !!commitError || authSettled;
  const attemptInFlight =
    hasVideo &&
    !!effectiveUrl &&
    (lastCheckedUrl === effectiveUrl || autoCheckedUrl === effectiveUrl) &&
    !attemptSettled;

  // A settled automatic attempt counts as checked, exactly like a clicked one:
  // Check Connectivity stays disabled until an input changes.
  useEffect(() => {
    if (autoAttemptLive && attemptSettled) {
      setLastCheckedUrl(effectiveUrl);
    }
  }, [autoAttemptLive, attemptSettled, effectiveUrl]);

  // Registration backstop: the CONNECTING backstop arms only once a preview
  // device exists, so an attempt whose device never registers (no deviceId, no
  // commit error) would show progress forever. It fails on the same horizon;
  // once a device appears, the device backstop takes over (and its reset effect
  // clears this latch). Per rule: settle-every-terminal-branch
  // (ai.knowledge/global) — every attempt ends.
  useEffect(() => {
    if (!attemptInFlight || deviceId) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setConnectionTimedOut(true);
      setConnectionFailed(true);
    }, CONNECTING_TIMEOUT_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [attemptInFlight, deviceId]);

  const n7AuthRejected =
    (authStatus === AuthenticationStatus.AUTHENTICATION_STATUS_INVALID || controlErrorAuthFail) &&
    probe.phase !== Phase.PROBE_EMPTY &&
    probe.phase !== Phase.PROBE_REAL;
  const n7AuthUnknown =
    authSettled && authStatus === AuthenticationStatus.AUTHENTICATION_STATUS_ERROR;
  const n7Failure =
    !videoIsPlaying &&
    probe.phase !== Phase.PROBE_EMPTY &&
    probe.phase !== Phase.PROBE_REAL &&
    !n7AuthRejected &&
    (commitError || connectionFailed || probeTimedOut || n7AuthUnknown)
      ? connectionTimedOut || probeTimedOut
        ? ALERT_VARIANT.NO_RESPONSE
        : ALERT_VARIANT.GENERAL
      : undefined;
  const seenN7Failure = useRef<string | undefined>(undefined);
  useEffect(() => {
    // Observe outcomes even while the switch loads: a skipped showing is not queued.
    const failure = n7Failure ? `${deviceId}` : undefined;
    if (failure !== seenN7Failure.current) {
      seenN7Failure.current = failure;
      if (
        n7Failure &&
        hasVideo &&
        !n7Retry.current &&
        effectiveUrl &&
        n7Attempt.current?.url === effectiveUrl &&
        n7Attempt.current.agentId === agentId
      ) {
        openN7(n7Failure);
      }
    }
    const retry = n7Retry.current;
    // Ignore the old preview's terminal state until the existing retry command
    // has replaced it, or preparation itself reports a terminal failure.
    // A preparation deadline settles the action even if raw cleanup is still
    // pending; previewPending continues to disable the button until it releases.
    const retrySettled =
      retry &&
      (videoIsPlaying ||
        (deviceId !== retry.deviceId && attemptSettled) ||
        previewAvailability === 'unavailable');
    if (retrySettled) {
      alertTracker.trackAlertActed(retry.showing, {
        action: ALERT_ACTION.TRY_AGAIN,
        result: videoIsPlaying ? ALERT_ACTION_RESULT.SUCCESS : ALERT_ACTION_RESULT.FAILURE
      });
      n7Retry.current = undefined;
      setN7Retrying(false);
    }
    if (videoIsPlaying) closeN7(ALERT_CLOSE_REASON.RESOLVED);
    else if (n7AuthRejected || !showN7) closeN7(ALERT_CLOSE_REASON.SUPERSEDED);
  }, [
    n7Failure,
    deviceId,
    hasVideo,
    attemptSettled,
    videoIsPlaying,
    n7AuthRejected,
    showN7,
    previewPending,
    previewAvailability,
    effectiveUrl,
    agentId
  ]);

  const retryN7 = () => {
    if (!n7Ref.current || n7Retry.current || previewPending) return;
    n7Retry.current = { showing: n7Ref.current, deviceId };
    setN7Retrying(true);
    clearConnectionFailure();
    dispatchProbe({ type: 'RESET' });
    writeOnVideoStep((draft) => {
      draft.video.isReady = false;
    });
    // Reuse the owned preview generation/cleanup path; the address is unchanged.
    requestPreviewRetry(producer);
  };

  const n7Title =
    n7?.alert_variant === ALERT_VARIANT.NOT_RTSP ? (
      <>
        The address needs to start with <span className='font-mono'>rtsp://</span>.
      </>
    ) : n7?.alert_variant === ALERT_VARIANT.NO_RESPONSE ? (
      <>
        No response from <span className='font-mono'>{streamUri?.host}</span>.
      </>
    ) : (
      "Couldn't start video."
    );
  const n7Message =
    n7?.alert_variant === ALERT_VARIANT.NOT_RTSP ? (
      <>
        For example, <span className='font-mono'>rtsp://192.168.1.10/stream1</span>
      </>
    ) : n7?.alert_variant === ALERT_VARIANT.NO_RESPONSE ? (
      'Check the IP address and port.'
    ) : (
      `Check the address, and that the camera is on the same network as ${bridgeName || 'the Bridge'}.`
    );

  // "Connection failed" body: a commit error's own (masked) message wins;
  // otherwise guidance chosen by what the encoder reported for the stream.
  const connectionFailureBody = commitError
    ? maskRtspCredentialsInMessage(
        commitError instanceof Error ? commitError.message : String(commitError)
      )
    : connectionFailureMessage(classifyConnectionFailure(videoError));

  // What the preview pane shows instead of the live preview, if anything. After a
  // failed attempt the notice agrees with the Callout — the live preview's own
  // badge reads "Camera Unreachable" even when the camera rejected the login.
  // N7 owns recovery while shown, so retain the placeholder through its retry
  // rather than mounting a second preview-failure action for the same problem.
  const previewNotice: T_Maybe<string> =
    authSettled && authStatus === AuthenticationStatus.AUTHENTICATION_STATUS_INVALID
      ? hasEnteredCreds
        ? PreviewAuthRejectedMessage
        : PreviewAuthRequiredMessage
      : showN7 && n7 && !videoIsPlaying
        ? n7Retrying
          ? AttemptingConnection
          : PreviewConnectionFailedMessage
        : hasVideo && (connectionFailed || probeTimedOut || !!commitError)
          ? PreviewConnectionFailedMessage
          : // The empty-creds probe's anonymous test stream is never shown.
            (hasVideo && probe.phase === Phase.PROBE_EMPTY) || autoAttemptPending
            ? AttemptingConnection
            : !hasVideo
              ? `Click the ${CheckConnectivity} button to view preview.`
              : undefined;

  return (
    <Columns {...props}>
      <Columns.Column>
        <h3 className='m-0'>Video settings</h3>

        <Column>
          <Group
            annotation={<Annotation.Required />}
            label={IpAddress}
            // The design-system Group message slot (error severity); the id ties
            // it to the input below.
            message={ipError ? <Message id={ipErrorId} message={ipError} /> : undefined}
          >
            <Input
              aria-describedby={ipError ? ipErrorId : undefined}
              aria-invalid={ipError ? true : undefined}
              focusOnMount
              onChange={setIpAddress}
              // Leaving the field (blur or Enter) marks it touched.
              onCommit={() => setIpTouched(true)}
              placeholder='11.22.33.44'
              value={ipAddress}
              variant={parsedIp.valid ? undefined : 'error'}
            />
          </Group>

          {isNotListedTyped ? (
            // ENG-4832 C3: the typed camera runs on the generic Other/Other
            // template (the format list below); "Change" reopens the flow.
            <>
              <Group label={CameraBrand}>
                <ReadOnlyValue value={NotListedBrandLabel} />
              </Group>
              <Group label={CameraModel}>
                <ReadOnlyValue
                  onChange={handleNotListed}
                  value={typedModel || NotListedBrandLabel}
                />
              </Group>
            </>
          ) : (
            // ENG-4830: the "Detected" pill marks what discovery resolved (brand-only
            // seed here) until the operator changes the field.
            <Group
              label={
                detected.brand ? <DetectedLabel field='brand' label={CameraBrand} /> : CameraBrand
              }
            >
              {/* Brand="Other" goes straight to the guided format list. The
                  "Enter Make & Model" prompt's primary action left the pick
                  uncommitted (no formats, no URL, Check disabled), so it is not
                  raised here; its recommendation is stated inline instead.
                  ENG-4832: "My camera isn't listed" opens the wizard's
                  not-listed flow; without an identification slice (older
                  fixtures) the picker falls back to the Other brand.
                  ENG-4831 B3: `models` + `onModelSelect` enable the combined
                  "Brand · Model" rows, committing brand and model in one pick. */}
              <Brand
                brands={brands}
                confirmOther={false}
                models={modelsByBrand}
                onModelSelect={handleCombinedChange}
                onNotListed={hasIdentification ? handleBrandNotListed : undefined}
                onQueryChange={setBrandQuery}
                selection={memoizedBrandSelection}
              />
              {otherBrand && <Annotation>{OtherBrandRecommendation}</Annotation>}
            </Group>
          )}

          {/* HIDE the Model selector on the Brand="Other" path (case 3) and in C3. */}
          {!otherBrand && !isNotListedTyped && (
            <Group
              label={
                detected.model ? <DetectedLabel field='model' label={CameraModel} /> : CameraModel
              }
            >
              <Model
                key={selection.brandId}
                brandId={selection.brandId}
                brandName={selectedBrand?.name}
                models={models}
                onNotListed={hasIdentification ? handleModelNotListed : undefined}
                // ENG-4831 A2: brand detected, model still to find — open the model
                // list ready to pick (while the brand is still the detected one).
                // Not focusOnMount: the IP field owns mount focus on this step.
                openOnMount={detected.brand && !entryDetected.model}
                selection={memoizedModelSelection}
              />
            </Group>
          )}

          {/* Credentials — optional (anonymous RTSP allowed). PM feedback pt 3:
              this block IS a <Column> (it used to be a bare <div>), so its three
              children sit on the same 16px rhythm as every other field instead
              of the header being welded at 0px to Device Username. Nesting a
              <Column> around only the two Groups would NOT fix that. Nested
              Columns are the house pattern (common/editor/credentials.tsx).
              role/aria-labelledby give the fieldset/legend semantic without a
              <fieldset>; the non-standard "optional" badge is gone. Title is a
              bold sub-label, NOT an <h4>: the design system strikes through
              h4-h6 (base.css "Don't use H4-H6"), which crossed out the word. */}
          <Column aria-labelledby='rtsp-credentials-title' role='group'>
            <span className='text-sm font-demibold' id='rtsp-credentials-title'>
              {Credentials}
            </span>

            <Group label={DeviceUsername}>
              <Input onChange={setUsername} placeholder={DeviceUsername} value={username} />
            </Group>

            <Group label={DevicePassword}>
              <Input
                onChange={setPassword}
                onCommit={handlePasswordCommit}
                placeholder={DevicePassword}
                type='password'
                value={password}
              />
            </Group>
          </Column>

          {/* Stream picker: Main/Sub for templates, the common-format catalog
              for Brand="Other". Hidden when there are no composable options.
              The Brand="Other" catalog renders as a radio-card list (title +
              IP-agnostic URL-template preview); the known-brand Main/Sub case
              keeps the plain Select dropdown. */}
          {options.length > 0 &&
            (otherBrand ? (
              <Group label={StreamFormatPicker}>
                <Annotation>{StreamFormatPickerHint}</Annotation>

                <div className='flex flex-col gap-8' role='radiogroup'>
                  {options.map((option) => {
                    const selected = selectedId === option.id;

                    return (
                      <button
                        aria-checked={selected}
                        className={twMerge(
                          'flex items-center gap-12 rounded bg-base-100 px-12 py-8 text-left border border-transparent',
                          selected && 'border-primary'
                        )}
                        key={option.id}
                        onClick={() => handleStreamSelection(option.id)}
                        role='radio'
                        type='button'
                      >
                        <span
                          className={twMerge(
                            'flex h-16 w-16 shrink-0 items-center justify-center rounded-full border',
                            selected ? 'border-primary' : 'border-secondary/50'
                          )}
                        >
                          {selected && <span className='h-8 w-8 rounded-full bg-primary' />}
                        </span>

                        <span className='flex flex-col'>
                          <span className='text-sm font-demibold'>{option.label}</span>
                          {option.preview && (
                            <span className='text-xs text-muted'>{option.preview}</span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </Group>
            ) : (
              <Group label={StreamFormat}>
                <Select.Root onValueChange={handleStreamSelection} value={selectedId}>
                  <Select.Trigger>
                    <Select.Value placeholder='Select a stream' />
                  </Select.Trigger>

                  <Select.Content>
                    {options.map((option) => (
                      <Select.Item key={option.id} value={option.id}>
                        {option.label}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </Group>
            ))}

          {/* Resulting URL — ONE always-editable, credential-FREE field (PM
              feedback pt 2: the read-only/masked mode and the pencil toggle are
              gone). The user edits host/port/path; the real user/pass come from
              the dedicated fields and are folded back in at compose/commit time
              (effectiveUrl above). Present in ALL brand cases — it is the
              resulting URL the screen composes.

              `aria-label` is a correctness fix, not decoration: <Group> renders
              its <Label> as a SIBLING with no htmlFor, so input.tsx falls back to
              the placeholder as the accessible name — and this placeholder
              CONTAINS the IP field's ('11.22.33.44'), which would make the two
              inputs ambiguous to assistive tech now that both are always mounted.
              A caller-supplied aria-label wins: `...props` is spread AFTER the
              placeholder fallback in input.tsx.

              No focusOnMount/selectOnMount: the IP field owns mount focus, and a
              second one would win by sibling order. No className either — the old
              read-only branch's `text-muted` would make an EDITABLE field read as
              disabled, and `flex-1` is inert now that the flex wrapper is gone
              (Input is already w-full). */}
          {showN7 && n7 && (
            <Callout
              severity='error'
              title={n7Title}
              role='alert'
              {...DataSTag(Sel.ConfigVideoDeviceVideo, 'rtsp.n7')}
            >
              <Callout.P>{n7Message}</Callout.P>
              {n7.alert_variant !== ALERT_VARIANT.NOT_RTSP && (
                <Button
                  className='focus-visible:ring-primary/50 mt-8 focus-visible:ring-2'
                  size='sm'
                  variant='tertiary'
                  disabled={n7Retrying || previewPending}
                  onClick={retryN7}
                  {...STagNew(Sel.ConfigVideoDeviceVideo, 'rtsp.try-again')}
                >
                  {n7Retrying && <Spinner />}Try again
                </Button>
              )}
            </Callout>
          )}
          <Group label={StreamUrl}>
            <Input
              aria-label={StreamUrl}
              aria-invalid={showN7 && !!n7 ? true : undefined}
              className={showN7 ? 'font-mono' : undefined}
              onCommit={validateN7Address}
              onChange={handleStreamUrlChange}
              placeholder='rtsp://11.22.33.44:554/sub/main'
              value={urlValue}
              // Non-empty gate: an untouched empty field is not an error, and
              // `urlValue && …` would infer `'' | 'error' | undefined` against
              // `Variant = 'error'`.
              variant={(showN7 ? !!n7 : urlValue.length > 0 && !isUrlValid) ? 'error' : undefined}
              // Per rule: playwright-stag-selectors-only (ai.knowledge/platform) — e2e must type the URL without placeholder/role selectors.
              {...STag(Sel.ConfigVideoDeviceVideo, 'stream-url')}
            />
          </Group>

          {isBridgeEnabled && (
            <Group label='Bridge'>
              <ManualSourceBridgeSelector onSelect={handleAgentChange} />
            </Group>
          )}
        </Column>

        <Button
          className='mt-8 self-start'
          disabled={!checkEnabled}
          onClick={handleCheckConnectivity}
          size='lg'
          variant='secondary'
          {...STagNew(Sel.ConfigVideoDeviceVideo, 'rtsp.check-connectivity')}
        >
          {CheckConnectivity}
        </Button>

        {/* Labeled progress for the attempt in flight (clicked or automatic),
            with a way out. Per rule: keyboard-reachable-interactive-elements
            (ai.knowledge/global) — the tertiary Cancel gets a visible focus ring. */}
        {attemptInFlight && (
          <div
            className='mt-8 flex items-center gap-8 text-sm text-muted'
            role='status'
            {...DataSTag(Sel.ConfigVideoDeviceVideo, 'rtsp.attempt-status')}
          >
            <span>{AttemptingConnection}</span>
            <Button
              className='focus-visible:ring-primary/50 focus-visible:ring-2'
              onClick={handleCancelAttempt}
              size='sm'
              variant='tertiary'
              {...STagNew(Sel.ConfigVideoDeviceVideo, 'rtsp.attempt-cancel')}
            >
              {CancelAttempt}
            </Button>
          </div>
        )}

        {/* Unified "Connection failed" Callout. Surfaces BOTH a candidate
            commit/sync exception (previews.video.error, set by mediator.tsx)
            AND a runtime stream failure (the live VIDEO connectionStatus that
            drives the preview's "Video Connection Failed" badge — latched
            sticky so it survives the encoder's FAILED→CONNECTING retry
            flicker). A commit error's own message wins; otherwise guidance
            that fits what failed (address vs stream path). */}
        {!showN7 && !!(commitError || connectionFailed || probeTimedOut) && !authCalloutVisible && (
          <Callout className='mt-16' severity='error' title='Connection failed'>
            <Callout.P {...DataSTag(Sel.ConfigVideoDeviceVideo, 'rtsp.connection-failed')}>
              {connectionFailureBody}
            </Callout.P>
          </Callout>
        )}

        {/* Video auth-status message (consume-only). Suppressed during BOTH probe
            phases: the empty-creds probe's INVALID is the discriminator signal,
            not a user-facing "invalid credentials" error, and during PROBE_REAL
            the read status is briefly the STALE Phase-1 verdict (mediator swap is
            async) — showing it would flash a phantom "Authentication failed". A
            genuine real-creds INVALID lands at DONE (phase no longer PROBE_REAL),
            where it renders. */}
        {/* A transient "Validating…" is an in-progress state, not a terminal
            result, and no longer surfaces its own message: authStatusMessage
            returns undefined for VALIDATING, so authMessage is only ever truthy
            for a terminal auth result (rejected / couldn't verify), which is what
            renders here. */}
        {!!authMessage &&
          !(showN7 && n7AuthUnknown) &&
          probe.phase !== Phase.PROBE_EMPTY &&
          probe.phase !== Phase.PROBE_REAL && (
            <Callout className='mt-16' severity='warning' title='Authentication'>
              <Callout.P>{authMessage}</Callout.P>
            </Callout>
          )}

        {/* Empty-creds-probe warning: no plane confirmed the entered credentials,
            and the copy says why — but the user may continue. Exact copy from the
            probeWarningMessage mapping. */}
        {!!probeWarning && (
          <Callout className='mt-16' severity='warning' title='Authentication'>
            <Callout.P>{probeWarning}</Callout.P>
          </Callout>
        )}

        {/* Control-plane credential rejection: advisory only — the video stream
            opened so Continue stays enabled; this just tells the user camera
            control auth failed. */}
        {!!probeControlError && (
          <Callout className='mt-16' severity='warning' title='Camera control'>
            <Callout.P>{probeControlError}</Callout.P>
          </Callout>
        )}
      </Columns.Column>

      <div>
        <h3>Video preview</h3>

        {/* The empty-creds probe writes a credential-free candidate.video (host, no
            creds) to test anonymous access, so gating on hasVideo alone would play
            that anonymous stream on screen; previewNotice covers PROBE_EMPTY so the
            user never sees the empty-creds test stream. */}
        {previewNotice ? <Placeholder message={previewNotice} /> : <Preview />}
      </div>
    </Columns>
  );
};
